/**
 * @file PayoutAdjustmentsSection.jsx
 * @module ui/src/components/tenant-admin/sections/PayoutAdjustmentsSection
 * @description
 *   Layer 2 payout adjustment modifiers — full S812 spec implementation.
 *
 * S813 FULL REBUILD — OI-S811-LAYER2-REGRESSION / S812 spec lock
 *   - D7: Progressive disclosure — available modifiers as compact "Add adjustment"
 *     cards; enabled modifiers as expanded configurable items; unavailable
 *     modifiers in a collapsed "Other adjustments not available for [spine]" disclosure.
 *   - D8: Reference-manifest + draftConfig pattern throughout. getValues() serialises
 *     to D8 canonical layer2Config manifest elements.
 *   - D10: Kicker applies_to = total_payout only; component_payout disabled.
 *   - PCMA_MATRIX: frontend constant, never a server call, never written at runtime.
 *   - All 9 modifiers grounded in ddl_authoring_extract_r60.md field tables.
 *   - OI constraints respected per mastercontext_s812_final.md OI registry.
 *
 * OI-S813-MODIFIER-SUBCOMPONENT-FILES: modifier sub-components implemented as
 *   named functions within this file (Option A) rather than separate files.
 *   Refactor to separate files when any modifier form exceeds ~150 lines or
 *   when OI-blocked features are unblocked (SPIFF tier schema, draw recovery schema).
 *
 * @version 2.4.0
 * @since S754
 * @changelog
 *   1.0.0  S754  Initial build
 *   1.2.0  S810  PCMA matrix + section-local form state
 *   2.0.0  S813  Full rebuild — D7/D8/D10, all 9 modifiers DDL-grounded
 *   2.1.0  S884  Three bug fixes:
 *   2.3.0  S913  G-01: MboModifier added (10th modifier). AD-106: authoring via
 *     D8 EP2b path — mbo_router.py CRUD endpoints NOT used for authoring.
 *     Supports binary and tiered scoring_method. mbo_tier rows written by
 *     _materialize_modifier() in plan_authoring_router.py when scoringMethod=tiered.
 *     BUG-1: RevTypeDifferentialsModifier — effectiveDate collected once per
 *       revenue_type (not per band) in draftConfig.bandDates dict.
 *       revenue_type_rate_table.effective_date is NOT NULL. All bands for a
 *       revenue_type share one effective_date (temporal versioning key, not a
 *       per-band config field). bandDates is a parallel key alongside bands —
 *       no shape change to the bands array. Backward compatible.
 *     BUG-2: ContestModifier — 'units_sold' rankMetric option removed.
 *       DDL r63 CHECK constraint only accepts: attainment_pct, credit_total,
 *       margin_total. 'units_sold' caused DB CHECK violation on INSERT.
 *       Replaced with 'margin_total'.
 *     BUG-3: DrawGuaranteeModifier — recoverable draw_type option disabled.
 *       OI-S812-DRAW-RECOVERY-SCHEMA: recovery_schedule_json schema absent.
 *       Recoverable draws created with NULL recovery_schedule_json trigger
 *       engine gate G-23 (DRAW_RECOVERY_SCHEDULE_MISSING) at run time.
 *       Re-enable when OI closes and recovery schedule form is implemented.
 *   2.4.0  S929  G-17/G-18: NpsModifier (11th) + AdoptionModifier (12th) added.
 *     D8 EP2b path — nps_router.py / adoption_router.py NOT used for authoring.
 *     nps_tier rows written by _materialize_modifier('nps') in plan_authoring_router.py.
 *     adoption_milestone rows written by _materialize_modifier('adoption').
 *     NPS and Adoption added to same PCMA_MATRIX spines as MBO (all non-bonus_pool).
 *     AdoptionModifier: business_unit_id collected per milestone (Option A — owner decision S929).
 *     DDL confirmed ddl_summary_r64.md: nps_tier, adoption_milestone schemas.
 */

import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// S895: draw ledger participant initialization panel (T3-5)
import DrawParticipantsPanel from './DrawParticipantsPanel';

// ---------------------------------------------------------------------------
// PCMA_MATRIX — imported from shared constants (S931 OI-S913-PCMA-MATRIX-DUPLICATE).
// Single source of truth in ui/src/constants/planConstants.js.
// ---------------------------------------------------------------------------
import { PCMA_MATRIX } from '../../../constants/planConstants';

// Canonical presentation order — UI display order only, NOT economic firing order.
// OI-S812-LAYER2-FIRING-ORDER: engine team must confirm canonical evaluation sequence.
// Do NOT encode economic firing order in the UI.
const MODIFIER_ORDER = [
  'rev_type_differentials',
  'multi_year_deal',
  'spiff',
  'contest',
  'kicker',
  'draw_guarantee',
  'cap',
  'clawback',
  'non_cash',
  'mbo',        // S913 G-01 — MBO objectives authoring (AD-106)
  'nps',        // S929 G-17 — NPS tier band authoring
  'adoption',   // S929 G-18 — Adoption milestone authoring
];

const MODIFIER_META = {
  rev_type_differentials: {
    label: 'Revenue Type Differentials',
    description: 'Different payout rates for New Logo, Expansion, Renewal, Upsell, and Churn Recovery deals.',
  },
  multi_year_deal: {
    label: 'Multi-Year Deal',
    description: 'How multi-year contract value is recognised and paid over the contract lifetime.',
  },
  spiff: {
    label: 'SPIFF / Tactical',
    description: 'Time-boxed or product-specific bonus paid as a separate payout.',
  },
  contest: {
    label: 'Contest',
    description: 'Time-boxed performance overlay with ranked prizes for top performers.',
  },
  kicker: {
    label: 'Kicker / Multiplier',
    description: 'Bonus multiplier applied to total payout when a performance threshold is exceeded.',
  },
  draw_guarantee: {
    label: 'Draw / Guarantee',
    description: 'Guaranteed floor payment — recoverable (advance against future earnings) or non-recoverable.',
  },
  cap: {
    label: 'Period Cap',
    description: 'Maximum cash payout ceiling for the full period. Applied after all other Layer 2 adjustments.',
  },
  clawback: {
    label: 'Churn Clawback',
    description: 'Recapture of previously paid commission when a deal churns within the protection window.',
  },
  non_cash: {
    label: 'Non-Cash Awards',
    description: 'Points, merchandise, travel, or recognition awards. Not cash payout. Not cap-subject.',
  },
  mbo: {
    label: 'MBO Objectives',
    description: 'Management by Objectives — define measurable goals with binary or tiered payout scoring.',
  },
  nps: {
    label: 'NPS Component',
    description: 'Net Promoter Score payout component — define score bands with payout factors (–100 to +100).',
  },
  adoption: {
    label: 'Adoption Milestones',
    description: 'Adoption milestone payout component — define achievement targets with payout factors per business unit.',
  },
};

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------
const S = {
  card:       { border: '1px solid rgb(var(--color-border))', borderRadius: 8, background: 'rgb(var(--color-surface))', overflow: 'hidden' },
  header:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderBottom: '1px solid rgb(var(--color-border))' },
  body:       { padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  field:      { display: 'flex', flexDirection: 'column', gap: 4 },
  label:      { fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' },
  input:      { border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' },
  select:     { border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' },
  grid2:      { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  muted:      { fontSize: 12, color: 'rgb(var(--color-text-muted))' },
  hint:       { fontSize: 12, color: 'rgb(var(--color-text-muted))', marginTop: 3 },
  error:      { fontSize: 12, color: 'rgb(var(--color-danger, #dc2626))', marginTop: 3 },
  blocked:    { fontSize: 12, color: 'rgb(var(--color-text-muted))', fontStyle: 'italic' },
  infoBox:    { background: 'rgb(var(--color-info, 59 130 246) / 0.07)', border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'rgb(var(--color-text-muted))' },
  warnBox:    { background: 'rgb(var(--color-warning, 234 179 8) / 0.08)', border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '8px 12px', fontSize: 12 },
  sectionH:   { fontSize: 13, fontWeight: 700, margin: '8px 0 4px', color: 'rgb(var(--color-text))' },
};

// ---------------------------------------------------------------------------
// Helper: nonEmpty
// ---------------------------------------------------------------------------
const nonEmpty = (v) => v != null && String(v).trim().length > 0;

// ---------------------------------------------------------------------------
// Field component
// ---------------------------------------------------------------------------
function Field({ label, hint, error, children, id }) {
  return (
    <div style={S.field}>
      {label && <label htmlFor={id} style={S.label}>{label}</label>}
      {children}
      {hint && !error && <span style={S.hint}>{hint}</span>}
      {error && <span style={S.error} role="alert">{error}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. KickerModifier
// DDL: kicker_plan (bigint PK) + kicker_rule [r60 confirmed]
// D10: applies_to = total_payout only; component_payout disabled
// OI-S812-KICKER-METRIC-QUALIFIER: metric_qualifier disabled
// ---------------------------------------------------------------------------
function KickerModifier({ draft, onChange, errors = {} }) {
  const plans = draft.plans ?? [{ planName: '', appliesTo: 'total_payout', effectiveFrom: '', effectiveTo: '', isActive: true, rules: [] }];

  const updatePlan = (i, field, value) => {
    const next = plans.map((p, idx) => idx === i ? { ...p, [field]: value } : p);
    onChange({ ...draft, plans: next });
  };

  const addRule = (planIdx) => {
    const next = plans.map((p, i) => i === planIdx ? {
      ...p,
      rules: [...(p.rules ?? []), { ruleName: '', metric: '', operator: '', thresholdValue: '', multiplierValue: '', conditionLogic: 'AND', isActive: true }]
    } : p);
    onChange({ ...draft, plans: next });
  };

  const updateRule = (planIdx, ruleIdx, field, value) => {
    const next = plans.map((p, i) => i !== planIdx ? p : {
      ...p,
      rules: (p.rules ?? []).map((r, j) => j === ruleIdx ? { ...r, [field]: value } : r),
    });
    onChange({ ...draft, plans: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {plans.map((plan, pi) => (
        <div key={pi} style={{ ...S.card, border: '1px solid rgb(var(--color-border))' }}>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={S.sectionH}>Kicker Plan {plans.length > 1 ? pi + 1 : ''}</p>
            <div style={S.grid2}>
              <Field label="Plan name *" id={`kicker-name-${pi}`} error={!nonEmpty(plan.planName) && errors[`plan${pi}.planName`]}>
                <input id={`kicker-name-${pi}`} style={S.input} value={plan.planName ?? ''} onChange={e => updatePlan(pi, 'planName', e.target.value)} placeholder="e.g. New Logo Kicker" />
              </Field>
              <Field label="Effective from *" id={`kicker-from-${pi}`}>
                <input id={`kicker-from-${pi}`} type="date" style={S.input} value={plan.effectiveFrom ?? ''} onChange={e => updatePlan(pi, 'effectiveFrom', e.target.value)} />
              </Field>
            </div>
            <Field label="Applies to" id={`kicker-applies-${pi}`} hint="Component-level kicker requires additional configuration — contact support.">
              {/* D10: total_payout only; component_payout disabled */}
              <select id={`kicker-applies-${pi}`} style={S.select} value="total_payout" disabled>
                <option value="total_payout">Total payout</option>
                <option value="component_payout" disabled>Component payout (not available in S813)</option>
              </select>
            </Field>

            <p style={S.sectionH}>Kicker Rules</p>
            {(plan.rules ?? []).length === 0 && (
              <p style={S.muted}>At least one rule is required.</p>
            )}
            {(plan.rules ?? []).map((rule, ri) => (
              <div key={ri} style={{ background: 'rgb(var(--color-surface-muted, var(--color-surface)))', borderRadius: 6, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={S.grid2}>
                  <Field label="Rule name *" id={`kicker-rule-name-${pi}-${ri}`}>
                    <input id={`kicker-rule-name-${pi}-${ri}`} style={S.input} value={rule.ruleName ?? ''} onChange={e => updateRule(pi, ri, 'ruleName', e.target.value)} placeholder="e.g. 110% attainment kicker" />
                  </Field>
                  <Field label="Metric *" id={`kicker-metric-${pi}-${ri}`}>
                    <select id={`kicker-metric-${pi}-${ri}`} style={S.select} value={rule.metric ?? ''} onChange={e => updateRule(pi, ri, 'metric', e.target.value)}>
                      <option value="">Select metric</option>
                      <option value="attainment_pct">Attainment %</option>
                      <option value="revenue_type_attainment">Revenue type attainment</option>
                      <option value="total_credit">Total credit</option>
                      <option value="component_payout">Component payout</option>
                    </select>
                  </Field>
                </div>
                <div style={S.grid2}>
                  <Field label="Operator *" id={`kicker-op-${pi}-${ri}`}>
                    <select id={`kicker-op-${pi}-${ri}`} style={S.select} value={rule.operator ?? ''} onChange={e => updateRule(pi, ri, 'operator', e.target.value)}>
                      <option value="">Select operator</option>
                      <option value="gte">≥ (at or above)</option>
                      <option value="gt">&gt; (above)</option>
                      <option value="lte">≤ (at or below)</option>
                      <option value="lt">&lt; (below)</option>
                      <option value="eq">= (exactly)</option>
                      <option value="between">Between</option>
                    </select>
                  </Field>
                  <Field label="Threshold *" id={`kicker-thresh-${pi}-${ri}`}>
                    <input id={`kicker-thresh-${pi}-${ri}`} type="number" style={S.input} value={rule.thresholdValue ?? ''} onChange={e => updateRule(pi, ri, 'thresholdValue', e.target.value)} placeholder="e.g. 110" />
                  </Field>
                </div>
                {rule.operator === 'between' && (
                  <Field label="Threshold 2 (upper bound) *" id={`kicker-thresh2-${pi}-${ri}`}>
                    <input id={`kicker-thresh2-${pi}-${ri}`} type="number" style={S.input} value={rule.thresholdValue2 ?? ''} onChange={e => updateRule(pi, ri, 'thresholdValue2', e.target.value)} />
                  </Field>
                )}
                <Field label="Multiplier *" id={`kicker-mult-${pi}-${ri}`} hint="e.g. 1.25 = 25% bonus on top of base payout">
                  <input id={`kicker-mult-${pi}-${ri}`} type="number" step="0.01" style={S.input} value={rule.multiplierValue ?? ''} onChange={e => updateRule(pi, ri, 'multiplierValue', e.target.value)} placeholder="e.g. 1.25" />
                </Field>
                {/* OI-S812-KICKER-METRIC-QUALIFIER: qualifier values undefined — disabled */}
                <p style={S.blocked}>Metric qualifier: not available in this release.</p>
              </div>
            ))}
            <button type="button" className="secondary-button" style={{ alignSelf: 'flex-start' }} onClick={() => addRule(pi)}>
              + Add rule
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. CapModifier — Period Cap
// DDL: plan_component.max_period_payout + max_period_payout_currency [r60 confirmed]
// OI-S812-PERIOD-CAP-BASIS: fixed monetary only — no % of OTE selector
// OI-S812-CAP-AUDIT-ENDPOINT: no audit panel
// ---------------------------------------------------------------------------
function CapModifier({ draft, onChange, errors = {} }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={S.infoBox}>
        Period cap limits total cash payout for the full period. Applied after all other adjustments.
        Deal-level cap is configured in Performance &amp; Rates alongside the rate table.
      </div>
      {/* OI-S812-PERIOD-CAP-BASIS: no % of OTE — ship fixed monetary only */}
      <div style={S.grid2}>
        <Field label="Period cap amount *" id="cap-amount" error={errors.maxPeriodPayout}>
          <input id="cap-amount" type="number" style={S.input} value={draft.maxPeriodPayout ?? ''}
            onChange={e => onChange({ ...draft, maxPeriodPayout: e.target.value })}
            placeholder="e.g. 500000" min="0" />
        </Field>
        <Field label="Currency *" id="cap-currency" error={errors.maxPeriodPayoutCurrency}>
          <input id="cap-currency" type="text" style={S.input} value={draft.maxPeriodPayoutCurrency ?? ''}
            onChange={e => onChange({ ...draft, maxPeriodPayoutCurrency: e.target.value.toUpperCase() })}
            placeholder="USD" maxLength={3} />
        </Field>
      </div>
      {/* OI-S812-CAP-AUDIT-ENDPOINT: post-run cap audit panel omitted */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. ClawbackModifier — Churn Clawback
// DDL: clawback_policy (bigint PK) [r60 confirmed]
// ---------------------------------------------------------------------------
function ClawbackModifier({ draft, onChange, errors = {} }) {
  const policies = draft.policies ?? [{ policyName: '', protectionWindowDays: 90, clawbackPct: '', appliesToRevenueTypes: '', effectiveFrom: '', isActive: true }];

  const update = (i, field, value) => {
    onChange({ ...draft, policies: policies.map((p, idx) => idx === i ? { ...p, [field]: value } : p) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {policies.map((pol, i) => (
        <div key={i} style={{ ...S.card }}>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={S.grid2}>
              <Field label="Policy name *" id={`cb-name-${i}`} error={errors[`policy${i}.policyName`]}>
                <input id={`cb-name-${i}`} style={S.input} value={pol.policyName ?? ''} onChange={e => update(i, 'policyName', e.target.value)} placeholder="e.g. Standard 90-day clawback" />
              </Field>
              <Field label="Effective from *" id={`cb-from-${i}`}>
                <input id={`cb-from-${i}`} type="date" style={S.input} value={pol.effectiveFrom ?? ''} onChange={e => update(i, 'effectiveFrom', e.target.value)} />
              </Field>
            </div>
            <div style={S.grid2}>
              <Field label="Protection window (days) *" id={`cb-window-${i}`} hint="Default: 90 days">
                <input id={`cb-window-${i}`} type="number" style={S.input} value={pol.protectionWindowDays ?? 90} onChange={e => update(i, 'protectionWindowDays', e.target.value)} min="1" />
              </Field>
              <Field label="Clawback % *" id={`cb-pct-${i}`} hint="0–100. 100 = full recovery.">
                <input id={`cb-pct-${i}`} type="number" style={S.input} value={pol.clawbackPct ?? ''} onChange={e => update(i, 'clawbackPct', e.target.value)} placeholder="e.g. 100" min="0" max="100" step="0.01" />
              </Field>
            </div>
            <Field label="Applies to revenue types" id={`cb-revtypes-${i}`} hint="Leave blank to apply to all types. Comma-separated codes (e.g. new_logo, expansion).">
              <input id={`cb-revtypes-${i}`} style={S.input} value={pol.appliesToRevenueTypes ?? ''} onChange={e => update(i, 'appliesToRevenueTypes', e.target.value)} placeholder="All types (blank = all)" />
            </Field>
          </div>
        </div>
      ))}
      <div style={S.infoBox}>
        Churn events are ingested separately via the Churn Events data pipeline. Individual event entry and CSV batch upload are both supported.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. RevTypeDifferentialsModifier
// DDL: revenue_type_rate_table (bigint PK) [r60 confirmed]
// OI-S812-REVTYPE-QUOTA-SCOPE: Quota Mapping tab = read-only explanation
// Revenue types: New Logo, Expansion, Renewal, Upsell, Churn Recovery
//
// BUG-1 FIX (S884): revenue_type_rate_table.effective_date date [NN] is a
// NOT NULL column. Previously the UI collected fromPct/toPct/payoutPct/
// interpolation per band but NOT effectiveDate, causing INSERT NOT NULL
// violations when the router wrote the rows.
//
// CORRECT MODEL: effective_date is ONE value per revenue_type, not per band.
// The UNIQUE constraint is (plan_component_id, revenue_type_code, band_sequence,
// effective_date) — meaning effective_date identifies a rate table VERSION, not
// an individual band configuration. All bands for a given revenue_type share the
// same effective_date (the plan start date for a new plan deployment).
//
// FIX: effectiveDate stored once per revenue_type in draftConfig.bandDates dict:
//   draftConfig.bandDates = { new_logo: '2026-01-01', expansion: '2026-01-01', ... }
//   draftConfig.bands     = { new_logo: [{fromPct, toPct, payoutPct, interpolation},...] }
//
// bandDates is a parallel key alongside bands — no shape change to bands array.
// Backward compatible: existing draftConfig without bandDates falls back to
// planEffectiveFrom (plan.effectiveFrom) so no persisted data is broken.
//
// Router instruction: write revenue_type_rate_table.effective_date from
// draftConfig.bandDates[revTypeCode] ?? plan.effectiveFrom for each row.
// ---------------------------------------------------------------------------
const REV_TYPES = [
  { code: 'new_logo',       label: 'New Logo' },
  { code: 'expansion',      label: 'Expansion' },
  { code: 'renewal',        label: 'Renewal' },
  { code: 'upsell',         label: 'Upsell' },
  { code: 'churn_recovery', label: 'Churn Recovery' },
];

function RevTypeDifferentialsModifier({ draft, onChange, plan }) {
  const bands     = draft.bands     ?? {};
  // BUG-1 FIX: bandDates stores one effectiveDate per revenue_type (not per band)
  const bandDates = draft.bandDates ?? {};
  const planEffectiveFrom = plan?.effectiveFrom ?? plan?.effective_from ?? '';

  const updateBandDate = (revType, value) => {
    onChange({ ...draft, bandDates: { ...bandDates, [revType]: value } });
  };

  const updateBand = (revType, idx, field, value) => {
    const typeBands = [...(bands[revType] ?? [])];
    typeBands[idx] = { ...typeBands[idx], [field]: value };
    onChange({ ...draft, bands: { ...bands, [revType]: typeBands } });
  };

  const addBand = (revType) => {
    const typeBands = [...(bands[revType] ?? [])];
    // Bands no longer carry effectiveDate — it lives in bandDates at type level
    typeBands.push({ fromPct: '', toPct: '', payoutPct: '', isAccelerator: false, interpolation: 'linear' });
    onChange({ ...draft, bands: { ...bands, [revType]: typeBands } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={S.infoBox}>
        Configure different payout rates per revenue type. Any revenue type without bands uses the plan's standard rate table.
        Bands must be contiguous and non-overlapping within each type.
        The effective date applies to all bands for that revenue type — it defaults to the plan start date.
      </div>
      {REV_TYPES.map(({ code, label }) => {
        const typeBands   = bands[code]     ?? [];
        // BUG-1 FIX: one effectiveDate per revenue_type, defaulting to plan.effectiveFrom
        const effectiveDate = bandDates[code] ?? planEffectiveFrom;
        return (
          <div key={code} style={S.card}>
            <div style={{ ...S.header, background: 'rgb(var(--color-surface-muted, var(--color-surface)))' }}>
              <strong style={{ fontSize: 13 }}>{label}</strong>
              <span style={S.muted}>{typeBands.length === 0 ? 'Uses standard rate table' : `${typeBands.length} band${typeBands.length !== 1 ? 's' : ''}`}</span>
            </div>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* BUG-1 FIX: effectiveDate collected once at revenue-type level.
                  All bands share this date. revenue_type_rate_table.effective_date [NN].
                  Shown only when this revenue type has at least one band configured. */}
              {typeBands.length > 0 && (
                <Field
                  label="Effective from *"
                  id={`rtd-effdate-${code}`}
                  hint="All bands for this revenue type share this effective date — defaults to plan start date"
                >
                  <input
                    id={`rtd-effdate-${code}`}
                    type="date"
                    style={{ ...S.input, maxWidth: 200 }}
                    value={effectiveDate}
                    onChange={e => updateBandDate(code, e.target.value)}
                  />
                </Field>
              )}
              {typeBands.map((band, bi) => (
                <div key={bi} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr) auto', gap: 8, alignItems: 'end' }}>
                  <Field label="From %" id={`rtd-from-${code}-${bi}`}>
                    <input id={`rtd-from-${code}-${bi}`} type="number" style={S.input} value={band.fromPct ?? ''} onChange={e => updateBand(code, bi, 'fromPct', e.target.value)} placeholder="0" />
                  </Field>
                  <Field label="To %" id={`rtd-to-${code}-${bi}`}>
                    <input id={`rtd-to-${code}-${bi}`} type="number" style={S.input} value={band.toPct ?? ''} onChange={e => updateBand(code, bi, 'toPct', e.target.value)} placeholder="100" />
                  </Field>
                  <Field label="Payout %" id={`rtd-pct-${code}-${bi}`}>
                    <input id={`rtd-pct-${code}-${bi}`} type="number" style={S.input} value={band.payoutPct ?? ''} onChange={e => updateBand(code, bi, 'payoutPct', e.target.value)} placeholder="0" />
                  </Field>
                  <Field label="Type" id={`rtd-interp-${code}-${bi}`}>
                    <select id={`rtd-interp-${code}-${bi}`} style={S.select} value={band.interpolation ?? 'linear'} onChange={e => updateBand(code, bi, 'interpolation', e.target.value)}>
                      <option value="linear">Linear</option>
                      <option value="step">Step</option>
                    </select>
                  </Field>
                </div>
              ))}
              <button type="button" className="secondary-button" style={{ alignSelf: 'flex-start', fontSize: 12 }} onClick={() => addBand(code)}>
                + Add band
              </button>
            </div>
          </div>
        );
      })}
      {/* OI-S812-REVTYPE-QUOTA-SCOPE: position_id NN — plan-level editor invalid */}
      <div style={S.warnBox}>
        <strong style={{ fontSize: 12 }}>Quota Mapping</strong>
        <p style={{ ...S.muted, marginTop: 4 }}>Revenue type quota allocation is position-specific and cannot be authored at the plan level. Quota mapping is configured through the Quota data pipeline per position per period.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5. SpiffModifier — SPIFF / Tactical
// DDL: spiff_plan (bigint PK) + spiff_plan_rule (bigint PK) [r63 confirmed S891]
// S892 Wave 3: OI-S812-SPIFF-TIER-SCHEMA CLOSED.
//   tier_schedule_json shape: [{tier, min_qty, max_qty, payout_amount}]
//   Confirmed: m075_spiff_incentives.sql lines 148–152.
//   max_qty: null = open-ended top tier.
// OD-13 / AD-56: Phase 1 UI cardinality = 1 rule per plan.
//   Backend (spiff_plan_rule) supports N — Phase 2 adds multi-rule authoring.
//   UI sends: plans[i].rules = [{ruleName, tiers:[...], productFamilyId?,
//     productLine?, sellingMotionId?, minDealAmount?}]
// ---------------------------------------------------------------------------
function SpiffModifier({ draft, onChange, errors = {} }) {
  const plans = draft.plans ?? [{ planName: '', triggerModel: '', effectiveFrom: '', payoutCurrency: '', payoutCap: '', isActive: true, rules: [{ ruleName: '', tiers: [], productFamilyId: null, productLine: '', sellingMotionId: null, minDealAmount: '' }] }];

  const updatePlan = (i, field, value) => {
    onChange({ ...draft, plans: plans.map((p, idx) => idx === i ? { ...p, [field]: value } : p) });
  };

  // Rule helpers — Phase 1: one rule per plan (OD-13). Array pattern preserved for Phase 2.
  const updateRule = (pi, field, value) => {
    const next = plans.map((p, idx) => {
      if (idx !== pi) return p;
      const rules = [...(p.rules ?? [{ ruleName: '', tiers: [] }])];
      rules[0] = { ...rules[0], [field]: value };
      return { ...p, rules };
    });
    onChange({ ...draft, plans: next });
  };

  // Tier helpers
  const addTier = (pi) => {
    const next = plans.map((p, idx) => {
      if (idx !== pi) return p;
      const rules = [...(p.rules ?? [])];
      const rule0 = rules[0] ?? { ruleName: '', tiers: [] };
      const tiers = rule0.tiers ?? [];
      const newTier = { tier: tiers.length + 1, minQty: '', maxQty: '', payoutAmount: '' };
      rules[0] = { ...rule0, tiers: [...tiers, newTier] };
      return { ...p, rules };
    });
    onChange({ ...draft, plans: next });
  };

  const updateTier = (pi, ti, field, value) => {
    const next = plans.map((p, idx) => {
      if (idx !== pi) return p;
      const rules = [...(p.rules ?? [])];
      const rule0 = { ...(rules[0] ?? { ruleName: '', tiers: [] }) };
      const tiers = (rule0.tiers ?? []).map((t, j) => j === ti ? { ...t, [field]: value } : t);
      rules[0] = { ...rule0, tiers };
      return { ...p, rules };
    });
    onChange({ ...draft, plans: next });
  };

  const removeTier = (pi, ti) => {
    const next = plans.map((p, idx) => {
      if (idx !== pi) return p;
      const rules = [...(p.rules ?? [])];
      const rule0 = { ...(rules[0] ?? { ruleName: '', tiers: [] }) };
      const tiers = (rule0.tiers ?? []).filter((_, j) => j !== ti)
        .map((t, j) => ({ ...t, tier: j + 1 })); // re-sequence tier numbers
      rules[0] = { ...rule0, tiers };
      return { ...p, rules };
    });
    onChange({ ...draft, plans: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {plans.map((plan, i) => {
        const rule0   = (plan.rules ?? [])[0] ?? { ruleName: '', tiers: [] };
        const tiers   = rule0.tiers ?? [];
        const isManagerAssigned = plan.triggerModel === 'manager_assigned';

        return (
          <div key={i} style={S.card}>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>

              {/* ── Plan header ── */}
              <div style={S.grid2}>
                <Field label="Plan name *" id={`spiff-name-${i}`} error={errors[`plan${i}.planName`]}>
                  <input id={`spiff-name-${i}`} style={S.input} value={plan.planName ?? ''}
                    onChange={e => updatePlan(i, 'planName', e.target.value)}
                    placeholder="e.g. Q3 Cloud Upsell SPIFF" />
                </Field>
                <Field label="Trigger model *" id={`spiff-trigger-${i}`}>
                  <select id={`spiff-trigger-${i}`} style={S.select} value={plan.triggerModel ?? ''}
                    onChange={e => updatePlan(i, 'triggerModel', e.target.value)}>
                    <option value="">Select trigger</option>
                    <option value="transaction_triggered">Transaction triggered</option>
                    <option value="manager_assigned">Manager assigned</option>
                  </select>
                </Field>
              </div>
              <div style={S.grid2}>
                <Field label="Effective from *" id={`spiff-from-${i}`}>
                  <input id={`spiff-from-${i}`} type="date" style={S.input} value={plan.effectiveFrom ?? ''}
                    onChange={e => updatePlan(i, 'effectiveFrom', e.target.value)} />
                </Field>
                <Field label="Payout currency *" id={`spiff-currency-${i}`}>
                  <input id={`spiff-currency-${i}`} style={S.input} value={plan.payoutCurrency ?? ''}
                    onChange={e => updatePlan(i, 'payoutCurrency', e.target.value.toUpperCase())}
                    placeholder="USD" maxLength={3} />
                </Field>
              </div>
              <Field label="Payout cap" id={`spiff-cap-${i}`}
                     hint="Optional maximum total payout for this SPIFF plan.">
                <input id={`spiff-cap-${i}`} type="number" style={S.input} value={plan.payoutCap ?? ''}
                  onChange={e => updatePlan(i, 'payoutCap', e.target.value)} placeholder="Optional" />
              </Field>

              {/* ── Tier schedule ── */}
              <p style={S.sectionH}>Tier schedule</p>

              {isManagerAssigned ? (
                <div style={S.infoBox}>
                  Manager-assigned SPIFF: award amounts are set by the manager at time of award.
                  Tier schedule is not used for this trigger model — awards are entered manually.
                </div>
              ) : (
                <>
                  {/* Qualification scope — optional, all nullable in DDL */}
                  <p style={{ ...S.muted, marginBottom: 0 }}>
                    Qualification scope (optional) — restrict this tier schedule to specific products or motions.
                    Leave blank to apply to all qualifying transactions.
                  </p>
                  <div style={S.grid2}>
                    <Field label="Product line" id={`spiff-prodline-${i}`}>
                      <input id={`spiff-prodline-${i}`} style={S.input} value={rule0.productLine ?? ''}
                        onChange={e => updateRule(i, 'productLine', e.target.value)}
                        placeholder="e.g. Security (optional)" />
                    </Field>
                    <Field label="Min deal amount" id={`spiff-mindeal-${i}`}
                           hint="Only transactions above this amount qualify (optional)">
                      <input id={`spiff-mindeal-${i}`} type="number" style={S.input}
                        value={rule0.minDealAmount ?? ''}
                        onChange={e => updateRule(i, 'minDealAmount', e.target.value)}
                        placeholder="Optional" min="0" />
                    </Field>
                  </div>

                  {/* Tier rows */}
                  {tiers.length === 0 && (
                    <p style={S.muted}>At least one tier is required. Add a tier to define the payout schedule.</p>
                  )}
                  {tiers.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {/* Column header */}
                      <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 1fr 1fr 32px', gap: 8, alignItems: 'center' }}>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>Tier</span>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>Min qty *</span>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>Max qty</span>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>Payout amount *</span>
                        <span />
                      </div>
                      {tiers.map((tier, ti) => (
                        <div key={ti} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 1fr 1fr 32px', gap: 8, alignItems: 'center' }}>
                          <span style={{ ...S.muted, textAlign: 'center', fontWeight: 600, fontSize: 13 }}>{ti + 1}</span>
                          <input
                            type="number" style={S.input} min="0" step="1"
                            value={tier.minQty ?? ''}
                            onChange={e => updateTier(i, ti, 'minQty', e.target.value)}
                            placeholder="e.g. 1"
                            aria-label={`Tier ${ti + 1} minimum quantity`}
                          />
                          <input
                            type="number" style={S.input} min="0" step="1"
                            value={tier.maxQty ?? ''}
                            onChange={e => updateTier(i, ti, 'maxQty', e.target.value || null)}
                            placeholder="blank = open"
                            aria-label={`Tier ${ti + 1} maximum quantity`}
                          />
                          <input
                            type="number" style={S.input} min="0" step="0.01"
                            value={tier.payoutAmount ?? ''}
                            onChange={e => updateTier(i, ti, 'payoutAmount', e.target.value)}
                            placeholder="e.g. 500"
                            aria-label={`Tier ${ti + 1} payout amount`}
                          />
                          <button
                            type="button"
                            onClick={() => removeTier(i, ti)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-danger, #dc2626))', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
                            aria-label={`Remove tier ${ti + 1}`}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button type="button" className="secondary-button" style={{ alignSelf: 'flex-start' }}
                      onClick={() => addTier(i)}>
                      + Add tier
                    </button>
                    {tiers.length > 0 && (
                      <span style={S.muted}>
                        Leave Max qty blank on the last tier to make it open-ended (no upper limit).
                      </span>
                    )}
                  </div>
                  <div style={S.infoBox}>
                    Tiers are non-overlapping quantity bands. The engine awards the payout for the tier
                    the qualifying transaction quantity falls into. Overlapping ranges are rejected at
                    calculation time (G-36).
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 6. DrawGuaranteeModifier — Draw / Guarantee
// DDL: draw_plan (bigint PK) [r60 confirmed]
//
// S891 Wave 2: OI-S812-DRAW-RECOVERY-SCHEMA CLOSED.
// recovery_schedule_json shape (owner-approved S891):
//   { frequency, grace_periods, duration_periods, recovery_cap_pct,
//     recovery_floor_amount, recovery_floor_currency }
// All fields CA-configurable. Engine (G-23) checks IS NOT NULL only.
// SP not yet extended to parse field names — stored for forward-compatible
// engine extension. BUG-3 disabled state removed — recoverable now enabled.
//
// Ledger display: INV-6 — draw balance is participant-scoped, not plan-scoped
// ---------------------------------------------------------------------------

function RecoveryScheduleForm({ schedule, onChange }) {
  // schedule = plan.recoverySchedule object (may be undefined on first enable)
  const rs = schedule ?? {};
  const upd = (field, value) => onChange({ ...rs, [field]: value });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8,
                  padding: 12, background: 'rgb(var(--color-surface-muted, var(--color-surface)))',
                  borderRadius: 6, border: '1px solid rgb(var(--color-border))' }}>
      <p style={{ ...S.sectionH, marginTop: 0 }}>Recovery schedule</p>
      <div style={S.infoBox}>
        Recovery schedule governs how the advance is recouped from future earnings.
        The engine will apply recovery each period according to these terms.
      </div>
      <div style={S.grid2}>
        <Field label="Recovery frequency *" id="rs-freq"
               hint="How often recovery is applied">
          <select id="rs-freq" style={S.select}
                  value={rs.frequency ?? 'monthly'}
                  onChange={e => upd('frequency', e.target.value)}>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </Field>
        <Field label="Grace periods" id="rs-grace"
               hint="Periods before recovery starts (0 = immediate)">
          <input id="rs-grace" type="number" style={S.input} min="0"
                 value={rs.gracePeriods ?? 0}
                 onChange={e => upd('gracePeriods', e.target.value)}
                 placeholder="0" />
        </Field>
      </div>
      <div style={S.grid2}>
        <Field label="Duration (periods) *" id="rs-duration"
               hint="How many periods recovery runs across">
          <input id="rs-duration" type="number" style={S.input} min="1"
                 value={rs.durationPeriods ?? ''}
                 onChange={e => upd('durationPeriods', e.target.value)}
                 placeholder="e.g. 12" />
        </Field>
        <Field label="Recovery cap % *" id="rs-cap"
               hint="Max % of earned commission withheld per period (0–100)">
          <input id="rs-cap" type="number" style={S.input} min="0" max="100" step="0.01"
                 value={rs.recoveryCapPct ?? ''}
                 onChange={e => upd('recoveryCapPct', e.target.value)}
                 placeholder="e.g. 50" />
        </Field>
      </div>
      <p style={{ ...S.muted, marginTop: 4 }}>
        Optional: rep must earn above this floor before any recovery applies.
        Leave blank to recover from first dollar earned.
      </p>
      <div style={S.grid2}>
        <Field label="Recovery floor amount" id="rs-floor-amt">
          <input id="rs-floor-amt" type="number" style={S.input} min="0"
                 value={rs.recoveryFloorAmount ?? ''}
                 onChange={e => upd('recoveryFloorAmount', e.target.value || null)}
                 placeholder="Optional" />
        </Field>
        <Field label="Floor currency" id="rs-floor-curr">
          <input id="rs-floor-curr" style={S.input} maxLength={3}
                 value={rs.recoveryFloorCurrency ?? ''}
                 onChange={e => upd('recoveryFloorCurrency', e.target.value.toUpperCase() || null)}
                 placeholder="USD" />
        </Field>
      </div>
    </div>
  );
}

// S895: plan prop added — carries planId, planStatus, fiscalPeriodId, userRole, managerId
// for DrawParticipantsPanel. Backward compatible — all fields optional with safe defaults.
function DrawGuaranteeModifier({ draft, onChange, errors = {}, plan = {}, readOnly = false }) {
  const plans = draft.plans ?? [{ planName: '', drawType: 'non_recoverable', advanceAmount: '', advanceCurrency: '', effectiveFrom: '', yearEndTreatment: 'carry_forward', isActive: true }];

  const update = (i, field, value) => {
    onChange({ ...draft, plans: plans.map((p, idx) => idx === i ? { ...p, [field]: value } : p) });
  };

  const updateRecoverySchedule = (i, rs) => {
    onChange({ ...draft, plans: plans.map((p, idx) => idx === i ? { ...p, recoverySchedule: rs } : p) });
  };

  // S895: DrawParticipantsPanel context — sourced from plan prop
  // planStatus: 'approved' gates participant panel visibility
  // userRole: 'comp_admin' | 'manager' — drives fetch path in DrawParticipantsPanel
  // managerId: required for manager EP45 fetch path (null for comp_admin)
  // fiscalPeriodId: forwarded into initialization body
  const planId        = plan?.planId        ?? null;
  const planStatus    = plan?.planStatus    ?? null;
  const userRole      = plan?.userRole      ?? 'comp_admin';
  const managerId     = plan?.managerId     ?? null;
  const fiscalPeriodId = plan?.fiscalPeriodId ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* S898: OI-S897-DRAW-PANEL-RENDER-GATE fix -- authoring fields suppressed in
          readOnly mode; DrawParticipantsPanel rendered unconditionally below (AD-64). */}
      {!readOnly && plans.map((p, i) => (
        <div key={i} style={S.card}>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={S.grid2}>
              <Field label="Plan name *" id={`draw-name-${i}`} error={errors[`plan${i}.planName`]}>
                <input id={`draw-name-${i}`} style={S.input} value={p.planName ?? ''}
                       onChange={e => update(i, 'planName', e.target.value)}
                       placeholder="e.g. New Hire Guarantee" />
              </Field>
              {/* S891 Wave 2: recoverable re-enabled — OI-S812-DRAW-RECOVERY-SCHEMA closed.
                  recovery_schedule_json written by router when draw_type='recoverable'. */}
              <Field label="Draw type *" id={`draw-type-${i}`}>
                <select id={`draw-type-${i}`} style={S.select}
                        value={p.drawType ?? 'non_recoverable'}
                        onChange={e => update(i, 'drawType', e.target.value)}>
                  <option value="non_recoverable">Non-recoverable (guaranteed floor)</option>
                  <option value="recoverable">Recoverable (advance against future earnings)</option>
                </select>
              </Field>
            </div>
            <div style={S.grid2}>
              <Field label="Advance amount *" id={`draw-amount-${i}`}>
                <input id={`draw-amount-${i}`} type="number" style={S.input}
                       value={p.advanceAmount ?? ''}
                       onChange={e => update(i, 'advanceAmount', e.target.value)}
                       placeholder="e.g. 5000" min="0" />
              </Field>
              <Field label="Currency *" id={`draw-currency-${i}`}>
                <input id={`draw-currency-${i}`} style={S.input}
                       value={p.advanceCurrency ?? ''}
                       onChange={e => update(i, 'advanceCurrency', e.target.value.toUpperCase())}
                       placeholder="USD" maxLength={3} />
              </Field>
            </div>
            <Field label="Effective from *" id={`draw-from-${i}`}>
              <input id={`draw-from-${i}`} type="date" style={S.input}
                     value={p.effectiveFrom ?? ''}
                     onChange={e => update(i, 'effectiveFrom', e.target.value)} />
            </Field>
            {/* OD-6 (S884): year_end_treatment NN — explicit policy required. */}
            <Field label="Year-end treatment *" id={`draw-yearend-${i}`}
                   hint="What happens to the outstanding draw balance at fiscal year close">
              <select id={`draw-yearend-${i}`} style={S.select}
                      value={p.yearEndTreatment ?? 'carry_forward'}
                      onChange={e => update(i, 'yearEndTreatment', e.target.value)}>
                <option value="carry_forward">Carry forward — balance moves to next year unchanged</option>
                <option value="forgive">Forgive — balance written off at year close, new year starts at $0</option>
                <option value="cap_carry_forward">Cap carry forward — balance carries forward up to a maximum</option>
                <option value="manual_settlement">Manual settlement — CA reviews and settles at year close</option>
              </select>
            </Field>
            {/* Recovery schedule — shown only for recoverable draws */}
            {p.drawType === 'recoverable' && (
              <RecoveryScheduleForm
                schedule={p.recoverySchedule}
                onChange={rs => updateRecoverySchedule(i, rs)}
              />
            )}
          </div>
        </div>
      ))}
      {/* INV-6: draw balance is participant-scoped, not plan-version-scoped */}
      <div style={S.infoBox}>
        Draw balance continues across plan versions and amendments.
        Balance is tracked per participant, not per plan version.
      </div>
      {/* S895: DrawParticipantsPanel — per-rep opening balance initialization.
          Shown when plan prop carries planId. Gracefully absent when planId is
          not available (template library deploy modal, etc.).
          S898 AD-64: intentionally renders on readOnly (approved governing) plans --
          participant initialization is an operational action, not plan authoring. */}
      {planId && (
        <DrawParticipantsPanel
          planId={planId}
          planStatus={planStatus ?? ''}
          userRole={userRole}
          managerId={managerId}
          fiscalPeriodId={fiscalPeriodId}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 7. ContestModifier
// DDL: contest (uuid PK) + contest_prize [r60 confirmed — P4-10]
// OI-S812-CONTEST-CODE: generation rule undefined → auto-suggest from name
// OI-S812-CONTEST-TIE-BREAK: tie_break_rule enum absent → text input
// OI-S812-CONTEST-CUSTOM-ELIGIBILITY: custom eligibility removed
// OI-S812-CONTEST-SPIFF-PRIZE-LINK: spiff_award prize type removed
// ---------------------------------------------------------------------------
const PRIZE_TYPES = [
  { value: 'cash',    label: 'Cash' },
  { value: 'non_cash', label: 'Non-Cash Award' },
  // spiff_award removed per OI-S812-CONTEST-SPIFF-PRIZE-LINK
];

function ContestModifier({ draft, onChange, errors = {}, plan }) {
  const contests = draft.contests ?? [{ contestName: '', contestCode: '', startDate: '', endDate: '', eligibilityType: 'role', rankMetric: '', tieBreakRule: '', prizes: [] }];

  const updateContest = (i, field, value) => {
    const next = contests.map((c, idx) => {
      if (idx !== i) return c;
      const updated = { ...c, [field]: value };
      // Auto-suggest contest code from name
      if (field === 'contestName' && !c.contestCode) {
        updated.contestCode = value.toUpperCase().replace(/[^A-Z0-9]/g, '_').slice(0, 60);
      }
      return updated;
    });
    onChange({ ...draft, contests: next });
  };

  const addPrize = (ci) => {
    const next = contests.map((c, i) => i !== ci ? c : {
      ...c, prizes: [...(c.prizes ?? []), { rankFrom: '', rankTo: '', prizeType: 'cash', cashAmount: '', cashCurrency: '' }]
    });
    onChange({ ...draft, contests: next });
  };

  const updatePrize = (ci, pi, field, value) => {
    const next = contests.map((c, i) => i !== ci ? c : {
      ...c, prizes: (c.prizes ?? []).map((p, j) => j !== pi ? p : { ...p, [field]: value })
    });
    onChange({ ...draft, contests: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {contests.map((contest, ci) => (
        <div key={ci} style={S.card}>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={S.grid2}>
              <Field label="Contest name *" id={`contest-name-${ci}`}>
                <input id={`contest-name-${ci}`} style={S.input} value={contest.contestName ?? ''} onChange={e => updateContest(ci, 'contestName', e.target.value)} placeholder="e.g. Q3 New Logo Sprint" />
              </Field>
              <Field label="Contest code *" id={`contest-code-${ci}`} hint="Auto-suggested from name. Must be unique.">
                <input id={`contest-code-${ci}`} style={S.input} value={contest.contestCode ?? ''} onChange={e => updateContest(ci, 'contestCode', e.target.value.toUpperCase())} placeholder="e.g. Q3_NEW_LOGO_SPRINT" />
              </Field>
            </div>
            <div style={S.grid2}>
              <Field label="Start date *" id={`contest-start-${ci}`}>
                <input id={`contest-start-${ci}`} type="date" style={S.input} value={contest.startDate ?? ''} onChange={e => updateContest(ci, 'startDate', e.target.value)} />
              </Field>
              <Field label="End date *" id={`contest-end-${ci}`}>
                <input id={`contest-end-${ci}`} type="date" style={S.input} value={contest.endDate ?? ''} onChange={e => updateContest(ci, 'endDate', e.target.value)} />
              </Field>
            </div>
            <div style={S.grid2}>
              <Field label="Eligibility type *" id={`contest-elig-${ci}`}>
                {/* custom removed per OI-S812-CONTEST-CUSTOM-ELIGIBILITY */}
                <select id={`contest-elig-${ci}`} style={S.select} value={contest.eligibilityType ?? ''} onChange={e => updateContest(ci, 'eligibilityType', e.target.value)}>
                  <option value="">Select</option>
                  <option value="role">Role</option>
                  <option value="segment">Segment</option>
                  <option value="pod">Pod</option>
                  <option value="business_unit">Business unit</option>
                </select>
              </Field>
              {/* BUG-2 FIX (S884): 'units_sold' removed — not in DDL r63 CHECK constraint.
                  ranking_plan.rank_metric CHECK: attainment_pct, credit_total, margin_total only.
                  'units_sold' caused DB CHECK violation on INSERT. */}
              <Field label="Rank metric *" id={`contest-metric-${ci}`}>
                <select id={`contest-metric-${ci}`} style={S.select} value={contest.rankMetric ?? ''} onChange={e => updateContest(ci, 'rankMetric', e.target.value)}>
                  <option value="">Select</option>
                  <option value="attainment_pct">Attainment %</option>
                  <option value="credit_total">Credit total</option>
                  <option value="margin_total">Margin total</option>
                </select>
              </Field>
            </div>
            {/* OI-S812-CONTEST-TIE-BREAK: enum confirmed from DDL r60 CHECK constraint S823.
                Values: earlier_close_date | higher_credit | alphabetical_id.
                Changed from free-text to constrained dropdown — free text fails DB CHECK. */}
            <Field label="Tie-break rule *" id={`contest-tiebreak-${ci}`}>
              <select id={`contest-tiebreak-${ci}`} style={S.select} value={contest.tieBreakRule ?? ''} onChange={e => updateContest(ci, 'tieBreakRule', e.target.value)}>
                <option value="">Select tie-break rule</option>
                <option value="earlier_close_date">Earlier close date</option>
                <option value="higher_credit">Higher credit total</option>
                <option value="alphabetical_id">Alphabetical (position ID)</option>
              </select>
            </Field>

            <p style={S.sectionH}>Prizes (at least 1 required)</p>
            {(contest.prizes ?? []).length === 0 && <p style={S.muted}>Add at least one prize tier.</p>}
            {(contest.prizes ?? []).map((prize, pi) => (
              <div key={pi} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <Field label="Rank from *" id={`prize-from-${ci}-${pi}`}>
                  <input id={`prize-from-${ci}-${pi}`} type="number" style={S.input} value={prize.rankFrom ?? ''} onChange={e => updatePrize(ci, pi, 'rankFrom', e.target.value)} placeholder="1" min="1" />
                </Field>
                <Field label="Rank to *" id={`prize-to-${ci}-${pi}`}>
                  <input id={`prize-to-${ci}-${pi}`} type="number" style={S.input} value={prize.rankTo ?? ''} onChange={e => updatePrize(ci, pi, 'rankTo', e.target.value)} placeholder="3" min="1" />
                </Field>
                <Field label="Prize type *" id={`prize-type-${ci}-${pi}`}>
                  <select id={`prize-type-${ci}-${pi}`} style={S.select} value={prize.prizeType ?? 'cash'} onChange={e => updatePrize(ci, pi, 'prizeType', e.target.value)}>
                    {PRIZE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                  </select>
                </Field>
                {prize.prizeType === 'cash' && (
                  <Field label="Cash amount *" id={`prize-cash-${ci}-${pi}`}>
                    <input id={`prize-cash-${ci}-${pi}`} type="number" style={S.input} value={prize.cashAmount ?? ''} onChange={e => updatePrize(ci, pi, 'cashAmount', e.target.value)} placeholder="e.g. 5000" />
                  </Field>
                )}
              </div>
            ))}
            <button type="button" className="secondary-button" style={{ alignSelf: 'flex-start', fontSize: 12 }} onClick={() => addPrize(ci)}>
              + Add prize tier
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8. NonCashModifier — Non-Cash Awards
// DDL: non_cash_award_type (uuid PK) [r60 confirmed — P4-11]
// No quantity_eligible — does not exist in r60 (OI-S812-NONCASH-QUANTITY)
// No fulfillment_method — does not exist in r60 (OI-S812-NONCASH-FULFILLMENT)
// Concept: award-type eligibility, NOT grants (grants are operational records)
// ---------------------------------------------------------------------------
function NonCashModifier({ draft, onChange }) {
  const awardTypeRefs = draft.awardTypeRefs ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={S.infoBox}>
        Non-cash awards are not cap-subject and are reported separately from cash payout.
        Select which award types participants under this plan are eligible for.
        Award grants (actual issuance) are operational records managed separately.
      </div>
      {awardTypeRefs.length === 0 && (
        <p style={S.muted}>No award types selected. At least one active award type is required.</p>
      )}
      {awardTypeRefs.map((ref, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))', borderRadius: 6 }}>
          <span style={{ fontSize: 13, flex: 1 }}>{ref.awardTypeName ?? ref.awardTypeId}</span>
          <span style={{ ...S.muted, fontSize: 11 }}>{ref.awardCategory ?? ''}</span>
          <button type="button" className="text-button" style={{ fontSize: 12 }} onClick={() => onChange({ ...draft, awardTypeRefs: awardTypeRefs.filter((_, j) => j !== i) })}>
            Remove
          </button>
        </div>
      ))}
      {/* Award type catalog browsing — future: open catalog drawer */}
      <div style={S.warnBox}>
        <p style={S.muted}>Award type catalog browsing will be available in a future release. Contact your administrator to configure award type eligibility.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 9. MultiYearDealModifier — Multi-Year Deal Policy
// DDL: multi_year_deal_policy (bigint PK) [r65 confirmed]
// policy_name varchar(200) NN — added post-r60; required by DB. UI now collects it.
// commission_rate numeric(6,4) NN default 0 — OI-S812-MYD-COMMISSION-RATE-SEMANTICS CLOSED.
//   Semantics confirmed: multiplier applied to commission on MYD deals (1.25 = 25% uplift).
//   Field now editable. Router reads commissionRate from draftConfig.
// ---------------------------------------------------------------------------
const PAYOUT_BASIS_OPTIONS = [
  { value: 'tcv',                      label: 'Total Contract Value (TCV)', description: 'Pay based on full contract value at close.' },
  { value: 'acv',                      label: 'Annual Contract Value (ACV)', description: 'Base compensation on annualized value.' },
  { value: 'amortized',                label: 'Spread Across Contract', description: 'Amortize recognition across configured periods.' },
  { value: 'year1_full_then_renewal',  label: 'Year 1 + Renewal', description: 'Year one at full rate, later years as renewal rate.' },
];

function MultiYearDealModifier({ draft, onChange, errors = {} }) {
  const policies = draft.policies ?? [{ policyName: '', payoutBasis: '', amortizationPeriods: '', maxContractYears: 5, capFutureCreditToCurrentQuota: false, effectiveFrom: '', commissionRate: '', isActive: true }];

  const update = (i, field, value) => {
    onChange({ ...draft, policies: policies.map((p, idx) => idx === i ? { ...p, [field]: value } : p) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {policies.map((pol, i) => (
        <div key={i} style={S.card}>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="Policy name *" id={`myd-name-${i}`} error={errors[`policy${i}.policyName`]}>
              <input id={`myd-name-${i}`} style={S.input} value={pol.policyName ?? ''}
                onChange={e => update(i, 'policyName', e.target.value)}
                placeholder="e.g. Standard MYD Policy" />
            </Field>
            <p style={S.sectionH}>Payout Basis *</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {PAYOUT_BASIS_OPTIONS.map(opt => (
                <label key={opt.value} style={{ display: 'flex', gap: 10, padding: 10, border: `1px solid ${pol.payoutBasis === opt.value ? 'rgb(var(--color-primary))' : 'rgb(var(--color-border))'}`, borderRadius: 8, cursor: 'pointer', background: pol.payoutBasis === opt.value ? 'rgb(var(--color-selected-bg))' : 'rgb(var(--color-surface))' }}>
                  <input type="radio" name={`myd-basis-${i}`} value={opt.value} checked={pol.payoutBasis === opt.value} onChange={() => update(i, 'payoutBasis', opt.value)} />
                  <span>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{opt.label}</span>
                    <span style={{ display: 'block', ...S.muted, marginTop: 2 }}>{opt.description}</span>
                  </span>
                </label>
              ))}
            </div>
            {pol.payoutBasis === 'amortized' && (
              <Field label="Amortization periods *" id={`myd-periods-${i}`}>
                <input id={`myd-periods-${i}`} type="number" style={S.input} value={pol.amortizationPeriods ?? ''} onChange={e => update(i, 'amortizationPeriods', e.target.value)} min="1" placeholder="e.g. 3" />
              </Field>
            )}
            <div style={S.grid2}>
              <Field label="Max contract years *" id={`myd-maxyr-${i}`} hint="Default: 5">
                <input id={`myd-maxyr-${i}`} type="number" style={S.input} value={pol.maxContractYears ?? 5} onChange={e => update(i, 'maxContractYears', e.target.value)} min="1" />
              </Field>
              <Field label="Effective from *" id={`myd-from-${i}`}>
                <input id={`myd-from-${i}`} type="date" style={S.input} value={pol.effectiveFrom ?? ''} onChange={e => update(i, 'effectiveFrom', e.target.value)} />
              </Field>
            </div>
            <Field label="Commission rate" id={`myd-commrate-${i}`}
              hint="Multiplier applied to commission on MYD deals. 1.25 = 25% uplift. Leave blank for no uplift (0).">
              <input id={`myd-commrate-${i}`} type="number" style={S.input} step="0.01" min="0"
                value={pol.commissionRate ?? ''}
                onChange={e => update(i, 'commissionRate', e.target.value)}
                placeholder="e.g. 1.25" />
            </Field>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={Boolean(pol.capFutureCreditToCurrentQuota)} onChange={e => update(i, 'capFutureCreditToCurrentQuota', e.target.checked)} />
              Cap future year credit to current quota year
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 10. MboModifier — MBO Objectives
// DDL: mbo_definition (mbo_definition_id bigserial PK) + mbo_tier [S912 confirmed]
//
// S913 G-01: AD-106 — authoring via D8 EP2b path. mbo_router.py is NOT called
// from this form. _materialize_modifier('mbo') in plan_authoring_router.py
// handles INSERT into mbo_definition + mbo_tier (for tiered scoring).
//
// scoring_method: 'binary' — objective is met (1.0) or not (0.0), no tiers needed.
//                 'tiered' — attainment % falls into a tier band, payoutFactor applied.
//
// mbo_weight per objective: fraction 0.0–1.0. Sum across all objectives for a
// plan_component must equal 1.0 (enforced by mbo_service.py MBO_WEIGHT_SUM_INVALID
// at submit time — not enforced in this form).
// ---------------------------------------------------------------------------
function MboModifier({ draft, onChange, errors = {} }) {
  const objectives = draft.objectives ?? [{ mboCode: '', mboName: '', mboWeight: '', scoringMethod: 'binary', effectiveDate: '', tiers: [] }];

  const updateObj = (i, field, value) => {
    onChange({ ...draft, objectives: objectives.map((o, idx) => idx === i ? { ...o, [field]: value } : o) });
  };

  const addObjective = () => {
    onChange({ ...draft, objectives: [...objectives, { mboCode: '', mboName: '', mboWeight: '', scoringMethod: 'binary', effectiveDate: '', tiers: [] }] });
  };

  const removeObjective = (i) => {
    if (objectives.length <= 1) return; // keep at least one
    onChange({ ...draft, objectives: objectives.filter((_, idx) => idx !== i) });
  };

  const addTier = (i) => {
    const next = objectives.map((o, idx) => {
      if (idx !== i) return o;
      const tiers = o.tiers ?? [];
      return { ...o, tiers: [...tiers, { tierSequence: tiers.length + 1, fromPct: '', toPct: '', payoutFactor: '', tierLabel: '' }] };
    });
    onChange({ ...draft, objectives: next });
  };

  const updateTier = (i, ti, field, value) => {
    const next = objectives.map((o, idx) => {
      if (idx !== i) return o;
      const tiers = (o.tiers ?? []).map((t, j) => j === ti ? { ...t, [field]: value } : t);
      return { ...o, tiers };
    });
    onChange({ ...draft, objectives: next });
  };

  const removeTier = (i, ti) => {
    const next = objectives.map((o, idx) => {
      if (idx !== i) return o;
      const tiers = (o.tiers ?? []).filter((_, j) => j !== ti)
        .map((t, j) => ({ ...t, tierSequence: j + 1 }));
      return { ...o, tiers };
    });
    onChange({ ...draft, objectives: next });
  };

  // S925 OI-S921-MBO-WEIGHT-SUBMISSION-GATE: running weight total.
  // Mirrors G6 gate in _run_full_semantic_validation() (plan_governance_router.py).
  // Tolerance +-0.0001 matches backend gate tolerance.
  const weightSum = objectives.reduce((acc, o) => {
    const w = parseFloat(o.mboWeight);
    return acc + (isNaN(w) ? 0 : w);
  }, 0);
  const weightSumPct = Math.round(weightSum * 10000) / 100; // e.g. 100.00
  const weightValid  = Math.abs(weightSum - 1.0) <= 0.0001;
  const weightEmpty  = objectives.every(o => !o.mboWeight);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={S.infoBox}>
        Define one or more MBO objectives for this plan component. Weights must sum to 1.0 across
        all objectives. Binary objectives pay out at 100% when the goal is met, 0% when not.
        Tiered objectives apply a payout factor based on attainment percentage bands.
      </div>

      {/* S925: Running weight total — G6 gate mirror */}
      {!weightEmpty && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', borderRadius: 8,
          background: weightValid
            ? 'rgb(var(--color-success, 22 163 74) / 0.07)'
            : 'rgb(var(--color-danger, 220 38 38) / 0.07)',
          border: `1px solid ${weightValid
            ? 'rgb(var(--color-success, 22 163 74) / 0.25)'
            : 'rgb(var(--color-danger, 220 38 38) / 0.25)'}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>
            Total weight: {weightSumPct.toFixed(2)}%
          </span>
          {weightValid ? (
            <span style={{ fontSize: 12, color: 'rgb(var(--color-success, 22 163 74))', fontWeight: 600 }}>
              ✓ Weights sum to 100% — plan can be submitted.
            </span>
          ) : (
            <span style={{ fontSize: 12, color: 'rgb(var(--color-danger, 220 38 38))', fontWeight: 600 }}>
              {weightSumPct < 100
                ? `${(100 - weightSumPct).toFixed(2)}% remaining — weights must total 100%.`
                : `${(weightSumPct - 100).toFixed(2)}% over — reduce objective weights.`}
            </span>
          )}
        </div>
      )}

      {objectives.map((obj, i) => {
        const isTimered = obj.scoringMethod === 'tiered';
        const tiers = obj.tiers ?? [];
        return (
          <div key={i} style={S.card}>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ ...S.sectionH, margin: 0 }}>Objective {i + 1}</p>
                {objectives.length > 1 && (
                  <button type="button" onClick={() => removeObjective(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-danger, #dc2626))', fontSize: 13 }}>
                    Remove
                  </button>
                )}
              </div>

              <div style={S.grid2}>
                <Field label="Objective code *" id={`mbo-code-${i}`} error={errors[`obj${i}.mboCode`]}>
                  <input id={`mbo-code-${i}`} style={S.input} value={obj.mboCode ?? ''}
                    onChange={e => updateObj(i, 'mboCode', e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                    placeholder="e.g. CUSTOMER_HEALTH" maxLength={60} />
                </Field>
                <Field label="Objective name *" id={`mbo-name-${i}`} error={errors[`obj${i}.mboName`]}>
                  <input id={`mbo-name-${i}`} style={S.input} value={obj.mboName ?? ''}
                    onChange={e => updateObj(i, 'mboName', e.target.value)}
                    placeholder="e.g. Customer Health Score" />
                </Field>
              </div>

              <div style={S.grid2}>
                <Field label="Weight *" id={`mbo-weight-${i}`}
                       hint="Fraction of MBO component (e.g. 0.5 = 50%). All objectives must sum to 1.0."
                       error={errors[`obj${i}.mboWeight`]}>
                  <input id={`mbo-weight-${i}`} type="number" style={S.input}
                    value={obj.mboWeight ?? ''}
                    onChange={e => updateObj(i, 'mboWeight', e.target.value)}
                    placeholder="e.g. 0.5" min="0" max="1" step="0.01" />
                </Field>
                <Field label="Effective date *" id={`mbo-effdate-${i}`}>
                  <input id={`mbo-effdate-${i}`} type="date" style={S.input}
                    value={obj.effectiveDate ?? ''}
                    onChange={e => updateObj(i, 'effectiveDate', e.target.value)} />
                </Field>
              </div>

              <Field label="Scoring method *" id={`mbo-scoring-${i}`}>
                <select id={`mbo-scoring-${i}`} style={S.select}
                  value={obj.scoringMethod ?? 'binary'}
                  onChange={e => updateObj(i, 'scoringMethod', e.target.value)}>
                  <option value="binary">Binary — met / not met</option>
                  <option value="tiered">Tiered — attainment % bands</option>
                </select>
              </Field>

              {isTimered && (
                <>
                  <p style={S.sectionH}>Attainment tiers</p>
                  <p style={{ ...S.muted, marginBottom: 0 }}>
                    Non-overlapping attainment bands. Engine applies the payout factor for the band
                    the rep&apos;s attainment falls into. Bands must satisfy: from &lt; to, sequence &gt; 0.
                  </p>
                  {tiers.length === 0 && (
                    <p style={S.muted}>At least one tier is required for tiered scoring.</p>
                  )}
                  {tiers.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 1fr 1fr 1fr 32px', gap: 8, alignItems: 'center' }}>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>Seq</span>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>From % *</span>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>To % *</span>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>Payout factor *</span>
                        <span style={{ ...S.muted, fontSize: 11, fontWeight: 600 }}>Label</span>
                        <span />
                      </div>
                      {tiers.map((tier, ti) => (
                        <div key={ti} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 1fr 1fr 1fr 32px', gap: 8, alignItems: 'center' }}>
                          <span style={{ ...S.muted, textAlign: 'center', fontWeight: 600, fontSize: 13 }}>{tier.tierSequence ?? ti + 1}</span>
                          <input type="number" style={S.input} min="0" max="1" step="0.01"
                            value={tier.fromPct ?? ''} onChange={e => updateTier(i, ti, 'fromPct', e.target.value)}
                            placeholder="0.00" aria-label={`Tier ${ti + 1} from attainment`} />
                          <input type="number" style={S.input} min="0" max="1" step="0.01"
                            value={tier.toPct ?? ''} onChange={e => updateTier(i, ti, 'toPct', e.target.value)}
                            placeholder="0.50" aria-label={`Tier ${ti + 1} to attainment`} />
                          <input type="number" style={S.input} min="0" max="2" step="0.01"
                            value={tier.payoutFactor ?? ''} onChange={e => updateTier(i, ti, 'payoutFactor', e.target.value)}
                            placeholder="e.g. 1.0" aria-label={`Tier ${ti + 1} payout factor`} />
                          <input type="text" style={S.input} maxLength={30}
                            value={tier.tierLabel ?? ''} onChange={e => updateTier(i, ti, 'tierLabel', e.target.value)}
                            placeholder="Optional" aria-label={`Tier ${ti + 1} label`} />
                          <button type="button"
                            onClick={() => removeTier(i, ti)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-danger, #dc2626))', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
                            aria-label={`Remove tier ${ti + 1}`}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="button" className="secondary-button" style={{ alignSelf: 'flex-start' }}
                    onClick={() => addTier(i)}>
                    + Add tier
                  </button>
                </>
              )}

              {!isTimered && (
                <div style={S.infoBox}>
                  Binary scoring: full payout factor (1.0) when objective is met, zero when not met.
                  No tier configuration required.
                </div>
              )}
            </div>
          </div>
        );
      })}

      <button type="button" className="secondary-button" style={{ alignSelf: 'flex-start' }}
        onClick={addObjective}>
        + Add objective
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// D8 normalizer — converts layer2Config manifest array to form state
// Manifest shape: { modifierType, enabled, entityRefs, draftConfig }
//
// OI-S814-CAP-DISPLAY-AFTER-MATERIALIZE fix:
// After cap materializes, draftConfig is null and entityRefs is populated.
// CapModifier reads from draftConfig — so we must seed draftConfig from
// planComponent.max_period_payout / max_period_payout_currency (EP2 data)
// when cap is materialized (entityRefs populated, draftConfig null).
// planComponent is passed as an optional second argument.
// ---------------------------------------------------------------------------
function initFromLayer2(layer2Config, planComponent) {
  // EP2 / DB returns layer2Config as either:
  //   (A) a raw array:            [{modifierType, enabled, entityRefs, draftConfig}, ...]
  //   (B) a wrapped object:       { modifiers: [{...}] }
  // Normalise to array before building the lookup map.
  const modifierArray = Array.isArray(layer2Config)
    ? layer2Config
    : (layer2Config?.modifiers ?? []);
  const stored = new Map(
    modifierArray.map((m) => [m.modifierType, m])
  );
  return Object.fromEntries(
    MODIFIER_ORDER.map((key) => {
      const m = stored.get(key);
      const entityRefs = m?.entityRefs ?? [];
      let draftConfig = m?.draftConfig ?? null;

      // OI-S814-CAP-DISPLAY-AFTER-MATERIALIZE:
      // Cap materializes to plan_component columns (Option A — D8 confirmed).
      // Post-materialization: draftConfig=null, entityRefs=[{entityType:'plan_component_period_cap',...}].
      // Seed the cap form from planComponent so the fields are not blank.
      // Also seed when draftConfig is an empty object ({}), which occurs when layer2_config
      // was written at CPL deploy time before a cap amount was entered (pre-materialization
      // state with enabled=true but no draftConfig values populated).
      const capDraftMissing = draftConfig == null ||
        (typeof draftConfig === 'object' && draftConfig.maxPeriodPayout == null);
      if (
        key === 'cap'
        && capDraftMissing
        && planComponent != null
        && (planComponent.max_period_payout != null || planComponent.maxPeriodPayout != null)
      ) {
        draftConfig = {
          maxPeriodPayout: planComponent.max_period_payout ?? planComponent.maxPeriodPayout ?? null,
          maxPeriodPayoutCurrency: planComponent.max_period_payout_currency ?? planComponent.maxPeriodPayoutCurrency ?? '',
        };
      }

      return [key, {
        enabled:    Boolean(m?.enabled ?? m?.isEnabled),
        entityRefs,
        draftConfig,
      }];
    })
  );
}

// Modifier types the backend plan_authoring_router accepts in layer2Config PATCH.
// 'nps' and 'adoption' are authored via separate routers (_materialize_modifier
// handles their DB rows independently) and must NOT be included in the layer2
// manifest sent to PATCH /api/v1/plans/:id — backend returns INVALID_MODIFIER_TYPE.
// 'mbo' is also excluded — authored via mbo_definition router, not layer2Config.
const BACKEND_LAYER2_MODIFIERS = new Set([
  'rev_type_differentials', 'multi_year_deal', 'spiff', 'contest',
  'kicker', 'draw_guarantee', 'cap', 'clawback', 'non_cash',
]);

// D8: serialize form state back to manifest elements for PATCH body
function serializeToManifest(form) {
  return MODIFIER_ORDER
    .filter((key) => BACKEND_LAYER2_MODIFIERS.has(key))
    .map((key) => {
      const entry = form[key];
      return {
        modifierType: key,
        enabled:      Boolean(entry?.enabled),
        entityRefs:   entry?.entityRefs ?? [],
        draftConfig:  entry?.draftConfig ?? null,
      };
    });
}

// Local modifierIsComplete — mirrors usePlanDocument.js logic for UI feedback
function modifierIsComplete(key, entry) {
  if (!entry?.enabled) return true; // not enabled = not blocking
  // If materialized (entityRefs present), treat as complete
  if (Array.isArray(entry.entityRefs) && entry.entityRefs.length > 0) return true;
  // Check draftConfig required fields by type
  const dc = entry.draftConfig;
  if (!dc) return false;
  switch (key) {
    case 'cap':
      return dc.maxPeriodPayout != null && Number(dc.maxPeriodPayout) > 0 && nonEmpty(dc.maxPeriodPayoutCurrency);
    case 'kicker':
      return Array.isArray(dc.plans) && dc.plans.length > 0 &&
        dc.plans.every(p => nonEmpty(p.planName) && nonEmpty(p.effectiveFrom) &&
          Array.isArray(p.rules) && p.rules.length > 0 &&
          p.rules.every(r => nonEmpty(r.ruleName) && nonEmpty(r.metric) && nonEmpty(r.operator) && r.thresholdValue != null && r.multiplierValue != null));
    case 'clawback':
      return Array.isArray(dc.policies) && dc.policies.length > 0 &&
        dc.policies.every(p => nonEmpty(p.policyName) && p.protectionWindowDays != null && p.clawbackPct != null && nonEmpty(p.effectiveFrom));
    case 'spiff':
      return Array.isArray(dc.plans) && dc.plans.length > 0 &&
        dc.plans.every(p => nonEmpty(p.planName) && nonEmpty(p.triggerModel) && nonEmpty(p.effectiveFrom) && nonEmpty(p.payoutCurrency));
    case 'draw_guarantee':
      return Array.isArray(dc.plans) && dc.plans.length > 0 &&
        dc.plans.every(p => nonEmpty(p.planName) && nonEmpty(p.drawType) && p.advanceAmount != null && Number(p.advanceAmount) > 0 && nonEmpty(p.advanceCurrency) && nonEmpty(p.effectiveFrom));
    case 'contest':
      return Array.isArray(dc.contests) && dc.contests.length > 0 &&
        dc.contests.every(c => nonEmpty(c.contestName) && nonEmpty(c.contestCode) && nonEmpty(c.startDate) && nonEmpty(c.endDate) && nonEmpty(c.eligibilityType) && nonEmpty(c.rankMetric) && nonEmpty(c.tieBreakRule) &&
          Array.isArray(c.prizes) && c.prizes.length > 0);
    case 'non_cash':
      return Array.isArray(dc.awardTypeRefs) && dc.awardTypeRefs.length > 0;
    case 'multi_year_deal':
      return Array.isArray(dc.policies) && dc.policies.length > 0 &&
        dc.policies.every(p => nonEmpty(p.policyName) && nonEmpty(p.payoutBasis) && p.maxContractYears != null && nonEmpty(p.effectiveFrom) &&
          (p.payoutBasis !== 'amortized' || p.amortizationPeriods != null));
    case 'rev_type_differentials':
      return dc.bands && Object.values(dc.bands).some(bands => Array.isArray(bands) && bands.length > 0);
    case 'mbo':
      return Array.isArray(dc.objectives) && dc.objectives.length > 0 &&
        dc.objectives.every(o => nonEmpty(o.mboCode) && nonEmpty(o.mboName) && o.mboWeight != null &&
          nonEmpty(o.scoringMethod) && nonEmpty(o.effectiveDate) &&
          (o.scoringMethod !== 'tiered' || (Array.isArray(o.tiers) && o.tiers.length > 0)));
    default: return false;
  }
}

// ---------------------------------------------------------------------------
// ModifierCard — D7 progressive disclosure wrapper
// Available + disabled: compact "Add adjustment" card with enable toggle
// Available + enabled: expanded with sub-form
// Unavailable: rendered only in the collapsed disclosure list
// ---------------------------------------------------------------------------
function ModifierCard({ modKey, entry, readOnly, onToggle, onDraftChange, plan, formErrors }) {
  const [expanded, setExpanded] = useState(Boolean(entry?.enabled));
  const meta = MODIFIER_META[modKey] ?? { label: modKey, description: '' };
  const isEnabled = Boolean(entry?.enabled);
  const draft = entry?.draftConfig ?? {};
  const isComplete = modifierIsComplete(modKey, entry);
  const hasMaterialized = Array.isArray(entry?.entityRefs) && entry.entityRefs.length > 0;

  const handleToggle = (checked) => {
    onToggle(modKey, checked);
    if (checked) setExpanded(true);
  };

  const handleDraftChange = (newDraft) => {
    onDraftChange(modKey, newDraft);
  };

  const modifierFormProps = { draft, onChange: handleDraftChange, errors: formErrors ?? {}, plan, readOnly };

  return (
    <article style={{ ...S.card, marginBottom: 0 }}>
      <header style={S.header}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ fontSize: 14 }}>{meta.label}</strong>
          {!isEnabled && <p style={{ ...S.muted, margin: '2px 0 0' }}>{meta.description}</p>}
          {isEnabled && hasMaterialized && <p style={{ ...S.muted, margin: '2px 0 0', fontSize: 11 }}>Saved to plan — editing updates draft.</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {isEnabled && (
            <span style={{ fontSize: 12, fontWeight: 600, color: isComplete ? 'rgb(var(--color-success, 22 163 74))' : 'rgb(var(--color-warning, 234 179 8))' }}>
              {isComplete ? '✓ Complete' : '! Incomplete'}
            </span>
          )}
          {!readOnly && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={isEnabled} onChange={e => handleToggle(e.target.checked)} />
              {isEnabled ? 'Enabled' : 'Add adjustment'}
            </label>
          )}
          {isEnabled && (
            <button type="button" className="text-button" style={{ fontSize: 12 }} onClick={() => setExpanded(e => !e)}>
              {expanded ? 'Collapse' : (readOnly ? 'View' : 'Configure')}
            </button>
          )}
        </div>
      </header>

      {isEnabled && expanded && !readOnly && (
        <div style={{ padding: 16, borderTop: '1px solid rgb(var(--color-border))' }}>
          {modKey === 'kicker'                 && <KickerModifier {...modifierFormProps} />}
          {modKey === 'cap'                    && <CapModifier {...modifierFormProps} />}
          {modKey === 'clawback'               && <ClawbackModifier {...modifierFormProps} />}
          {modKey === 'rev_type_differentials' && <RevTypeDifferentialsModifier {...modifierFormProps} />}
          {modKey === 'spiff'                  && <SpiffModifier {...modifierFormProps} />}
          {modKey === 'draw_guarantee'         && <DrawGuaranteeModifier {...modifierFormProps} />}
          {modKey === 'contest'                && <ContestModifier {...modifierFormProps} />}
          {modKey === 'non_cash'               && <NonCashModifier {...modifierFormProps} />}
          {modKey === 'multi_year_deal'        && <MultiYearDealModifier {...modifierFormProps} />}
          {modKey === 'mbo'                    && <MboModifier {...modifierFormProps} />}
          {modKey === 'nps'                    && <NpsModifier {...modifierFormProps} />}
          {modKey === 'adoption'               && <AdoptionModifier {...modifierFormProps} />}
        </div>
      )}

      {isEnabled && readOnly && (
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={S.muted}>Configuration is read-only. Changes require an amendment.</p>

          {/* rev_type_differentials — band summary per revenue type */}
          {modKey === 'rev_type_differentials' && (() => {
            const bands     = draft.bands     ?? {};
            const bandDates = draft.bandDates ?? {};
            const REV_TYPE_LABELS = {
              new_logo: 'New Logo', expansion: 'Expansion', renewal: 'Renewal',
              upsell: 'Upsell', churn_recovery: 'Churn Recovery',
            };
            const configured = Object.entries(bands).filter(([, bl]) => Array.isArray(bl) && bl.length > 0);
            if (configured.length === 0) {
              return <p style={S.muted}>No custom bands configured — all types use the standard rate table.</p>;
            }
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {configured.map(([code, bl]) => {
                  const payouts = bl.map(b => Number(b.payoutPct)).filter(v => !isNaN(v));
                  const min = Math.min(...payouts);
                  const max = Math.max(...payouts);
                  const pctRange = payouts.length === 0 ? '—'
                    : min === max ? `${(min * 100).toFixed(0)}%`
                    : `${(min * 100).toFixed(0)}% – ${(max * 100).toFixed(0)}%`;
                  const effDate = bandDates[code] ?? null;
                  return (
                    <div key={code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                        padding: '6px 10px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))',
                        borderRadius: 6, border: '1px solid rgb(var(--color-border))' }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{REV_TYPE_LABELS[code] ?? code}</span>
                      <span style={S.muted}>
                        {bl.length} band{bl.length !== 1 ? 's' : ''} · payout {pctRange}
                        {effDate ? ` · eff. ${effDate}` : ''}
                      </span>
                    </div>
                  );
                })}
                {Object.entries(REV_TYPE_LABELS)
                  .filter(([code]) => !(bands[code]?.length > 0))
                  .map(([code, label]) => (
                    <div key={code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                        padding: '6px 10px', borderRadius: 6, border: '1px solid rgb(var(--color-border))' }}>
                      <span style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))' }}>{label}</span>
                      <span style={S.muted}>Uses standard rate table</span>
                    </div>
                  ))}
              </div>
            );
          })()}

          {/* kicker — plan + rule summary */}
          {modKey === 'kicker' && (() => {
            const plans = draft.plans ?? [];
            if (plans.length === 0) return <p style={S.muted}>No kicker plans configured.</p>;
            const METRIC_LABELS = {
              attainment_pct: 'Attainment %', revenue_type_attainment: 'Rev type attainment',
              total_credit: 'Total credit', component_payout: 'Component payout',
            };
            const OP_LABELS = { gte: '≥', gt: '>', lte: '≤', lt: '<', eq: '=', between: 'between' };
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {plans.map((p, i) => (
                  <div key={i} style={S.card}>
                    <div style={{ ...S.header, background: 'rgb(var(--color-surface-muted, var(--color-surface)))' }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{p.planName || `Kicker Plan ${i + 1}`}</span>
                      <span style={S.muted}>eff. {p.effectiveFrom || '—'}</span>
                    </div>
                    <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(p.rules ?? []).map((r, ri) => (
                        <div key={ri} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{r.ruleName || `Rule ${ri + 1}`}</span>
                          <span style={S.muted}>
                            {METRIC_LABELS[r.metric] ?? r.metric} {OP_LABELS[r.operator] ?? r.operator} {r.thresholdValue}
                            {r.operator === 'between' && r.thresholdValue2 ? ` – ${r.thresholdValue2}` : ''}
                            {r.multiplierValue ? ` → ×${r.multiplierValue}` : ''}
                          </span>
                        </div>
                      ))}
                      {(p.rules ?? []).length === 0 && <span style={S.muted}>No rules defined.</span>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* cap — amount + currency */}
          {modKey === 'cap' && (
            <div style={{ padding: '8px 12px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))',
                borderRadius: 6, border: '1px solid rgb(var(--color-border))' }}>
              {draft.maxPeriodPayout != null
                ? <span style={{ fontSize: 13 }}>Period cap: <strong>{Number(draft.maxPeriodPayout).toLocaleString()} {draft.maxPeriodPayoutCurrency ?? ''}</strong></span>
                : <span style={S.muted}>Cap amount not set.</span>}
            </div>
          )}

          {/* clawback — policy summary */}
          {modKey === 'clawback' && (() => {
            const policies = draft.policies ?? [];
            if (policies.length === 0) return <p style={S.muted}>No clawback policies configured.</p>;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {policies.map((p, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))',
                      borderRadius: 6, border: '1px solid rgb(var(--color-border))', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{p.policyName || `Policy ${i + 1}`}</span>
                    <span style={S.muted}>
                      {p.clawbackPct != null ? `${p.clawbackPct}% recovery` : ''}
                      {p.protectionWindowDays ? ` · ${p.protectionWindowDays}-day window` : ''}
                      {p.effectiveFrom ? ` · eff. ${p.effectiveFrom}` : ''}
                    </span>
                    {p.appliesToRevenueTypes
                      ? <span style={S.muted}>Applies to: {p.appliesToRevenueTypes}</span>
                      : <span style={S.muted}>Applies to: all revenue types</span>}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* multi_year_deal — policy summary */}
          {modKey === 'multi_year_deal' && (() => {
            const policies = draft.policies ?? [];
            if (policies.length === 0) return <p style={S.muted}>No multi-year deal policy configured.</p>;
            const BASIS_LABELS = {
              full_upfront: 'Full upfront', amortized: 'Amortized', first_year_only: 'First year only',
            };
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {policies.map((p, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))',
                      borderRadius: 6, border: '1px solid rgb(var(--color-border))', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{p.policyName || `Policy ${i + 1}`}</span>
                    <span style={S.muted}>
                      {BASIS_LABELS[p.payoutBasis] ?? p.payoutBasis}
                      {p.maxContractYears ? ` · max ${p.maxContractYears} yr` : ''}
                      {p.commissionRate != null ? ` · rate ${p.commissionRate}` : ''}
                      {p.payoutBasis === 'amortized' && p.amortizationPeriods ? ` · ${p.amortizationPeriods} periods` : ''}
                      {p.effectiveFrom ? ` · eff. ${p.effectiveFrom}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* spiff — plan + tier summary */}
          {modKey === 'spiff' && (() => {
            const plans = draft.plans ?? [];
            if (plans.length === 0) return <p style={S.muted}>No SPIFF plans configured.</p>;
            const TRIGGER_LABELS = { transaction_triggered: 'Transaction triggered', manager_assigned: 'Manager assigned' };
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {plans.map((p, i) => {
                  const tiers = (p.rules ?? [])[0]?.tiers ?? [];
                  return (
                    <div key={i} style={S.card}>
                      <div style={{ ...S.header, background: 'rgb(var(--color-surface-muted, var(--color-surface)))' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{p.planName || `SPIFF ${i + 1}`}</span>
                        <span style={S.muted}>{TRIGGER_LABELS[p.triggerModel] ?? p.triggerModel} · {p.payoutCurrency}</span>
                      </div>
                      <div style={{ padding: '8px 12px' }}>
                        <span style={S.muted}>
                          eff. {p.effectiveFrom || '—'}
                          {p.payoutCap ? ` · cap ${Number(p.payoutCap).toLocaleString()} ${p.payoutCurrency}` : ''}
                          {tiers.length > 0 ? ` · ${tiers.length} tier${tiers.length !== 1 ? 's' : ''}` : ' · no tiers (manager-assigned)'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* mbo — objective list */}
          {modKey === 'mbo' && (() => {
            const objectives = draft.objectives ?? [];
            if (objectives.length === 0) return <p style={S.muted}>No MBO objectives configured.</p>;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {objectives.map((o, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                      padding: '6px 10px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))',
                      borderRadius: 6, border: '1px solid rgb(var(--color-border))' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{o.mboName || o.mboCode || `Objective ${i + 1}`}</span>
                    <span style={S.muted}>
                      {o.mboWeight != null ? `${Math.round(Number(o.mboWeight) * 100)}% weight` : ''}
                      {o.scoringMethod ? ` · ${o.scoringMethod}` : ''}
                      {o.tiers?.length > 0 ? ` · ${o.tiers.length} tier${o.tiers.length !== 1 ? 's' : ''}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* nps — score bands */}
          {modKey === 'nps' && (() => {
            const tiers = draft.tiers ?? [];
            if (tiers.length === 0) return <p style={S.muted}>No NPS bands configured.</p>;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tiers.map((t, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                      padding: '6px 10px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))',
                      borderRadius: 6, border: '1px solid rgb(var(--color-border))' }}>
                    <span style={{ fontSize: 13 }}>
                      {t.tierLabel || `Band ${i + 1}`}
                      <span style={{ ...S.muted, marginLeft: 8 }}>score {t.fromScore} to {t.toScore}</span>
                    </span>
                    <span style={S.muted}>factor {t.payoutFactor}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* adoption — milestone list */}
          {modKey === 'adoption' && (() => {
            const milestones = draft.milestones ?? [];
            if (milestones.length === 0) return <p style={S.muted}>No adoption milestones configured.</p>;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {milestones.map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                      padding: '6px 10px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))',
                      borderRadius: 6, border: '1px solid rgb(var(--color-border))' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{m.milestoneName || m.milestoneCode || `Milestone ${i + 1}`}</span>
                    <span style={S.muted}>
                      target {m.milestoneTarget} {m.milestoneUnit}
                      {m.payoutFactor != null ? ` · factor ${m.payoutFactor}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* S898: OI-S897-DRAW-PANEL-RENDER-GATE fix -- DrawParticipantsPanel is an
              operational action (opening balance init), not plan authoring. Render
              DrawGuaranteeModifier in readOnly mode so the panel appears on approved
              governing plans regardless of edit-lock state. AD-64. */}
          {modKey === 'draw_guarantee' && (
            <DrawGuaranteeModifier {...modifierFormProps} />
          )}
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// UnavailableDisclosure — D7: collapsed list of unavailable modifiers
// ---------------------------------------------------------------------------
function UnavailableDisclosure({ unavailableKeys, spineName }) {
  const [open, setOpen] = useState(false);
  if (unavailableKeys.length === 0) return null;
  return (
    <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 8, overflow: 'hidden' }}>
      <button
        type="button"
        style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 0, padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: 'rgb(var(--color-text-muted))', display: 'flex', justifyContent: 'space-between' }}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span>Other adjustments not available for {spineName} ({unavailableKeys.length})</span>
        <span aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <ul style={{ margin: 0, padding: '0 14px 12px', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {unavailableKeys.map(key => {
            const meta = MODIFIER_META[key] ?? { label: key, description: '' };
            return (
              <li key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text-muted))' }}>{meta.label}</span>
                <span style={S.muted}>{meta.description}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SPINE_LABELS — for unavailable disclosure heading
// ---------------------------------------------------------------------------
const SPINE_LABELS = {
  revenue_bookings:       'Revenue / Bookings',
  transaction_commission: 'Transaction Commission',
  volume_quantity:        'Volume / Quantity',
  market_share:           'Market Share / Penetration',
  grr_nrr_retention:      'Retention (GRR / NRR)',
  mbo_objective:          'MBO / Objective',
  margin_profit:          'Margin / Profit',
  bonus_pool:             'Bonus Pool / Allocation',
  pharma_script:          'Pharma / Script Volume',
};

// ---------------------------------------------------------------------------
// PayoutAdjustmentsSection — main forwardRef orchestrator
// ---------------------------------------------------------------------------
const PayoutAdjustmentsSection = forwardRef(function PayoutAdjustmentsSection({
  plan,
  layer2Config,
  pcmaMatrix,
  readOnly,
}, ref) {
  // OI-S814-CAP-DISPLAY-AFTER-MATERIALIZE: pass plan.planComponent so initFromLayer2
  // can seed the cap form from max_period_payout when cap is post-materialization state.
  const planComponent = plan?.planComponent ?? null;

  const [form, setForm] = useState(() => initFromLayer2(layer2Config, planComponent));

  // Reset only on plan change — planComponent passed so cap re-seeds after materialization
  useEffect(() => {
    setForm(initFromLayer2(layer2Config, planComponent));
  }, [plan.planId]); // eslint-disable-line react-hooks/exhaustive-deps

  // D8: getValues() serializes to canonical layer2Config manifest
  useImperativeHandle(ref, () => ({
    getValues() {
      const manifest = serializeToManifest(form);
      // BUG-3 fix (S823): inject plan.fiscalPeriodId into every contest draftConfig.
      // Router _materialize_modifier("contest") requires fiscal_period_id [NN] in
      // draftConfig to insert the contest row. UI never collected it — injected here
      // from plan prop so the field is always present when the plan is known.
      const fiscalPeriodId = plan?.fiscalPeriodId ?? plan?.fiscal_period_id ?? null;
      const patched = manifest.map((elem) => {
        if (elem.modifierType !== 'contest' || !elem.draftConfig || fiscalPeriodId == null) {
          return elem;
        }
        const contests = (elem.draftConfig.contests ?? []).map((c) => ({
          ...c,
          fiscalPeriodId,
        }));
        return { ...elem, draftConfig: { ...elem.draftConfig, contests } };
      });
      return { layer2Config: patched };
    },
  }), [form, plan]);

  // PCMA_MATRIX: derive available modifiers from plan.primarySpine
  // pcmaMatrix prop takes precedence if explicitly provided by canvas
  const spine = plan?.primarySpine ?? null;
  const spineName = SPINE_LABELS[spine] ?? spine ?? 'this plan';
  const available = new Set(
    (pcmaMatrix?.availableModifiers?.length > 0
      ? pcmaMatrix.availableModifiers
      : PCMA_MATRIX[spine] ?? [])
  );

  const availableInOrder = MODIFIER_ORDER.filter(k => available.has(k));
  const unavailableInOrder = MODIFIER_ORDER.filter(k => !available.has(k));

  const handleToggle = (key, checked) => {
    setForm(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        enabled: checked,
        draftConfig: checked ? (prev[key]?.draftConfig ?? getDefaultDraft(key)) : null,
      },
    }));
  };

  const handleDraftChange = (key, newDraft) => {
    setForm(prev => ({
      ...prev,
      [key]: { ...prev[key], draftConfig: newDraft },
    }));
  };

  const enabledCount = availableInOrder.filter(k => form[k]?.enabled).length;

  return (
    <section style={{ fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>
      <header style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px' }}>Adjustments</h3>
        <p style={{ ...S.muted, margin: 0 }}>
          Layer 2 adjustments modify the base payout from Performance &amp; Rates.
          Enable the adjustments that apply to this plan.
          {enabledCount === 0 && ' No adjustments configured — this section is optional.'}
        </p>
      </header>

      {readOnly && (
        <div style={{ ...S.infoBox, marginBottom: 12 }}>
          Adjustments configuration is read-only for this plan version. Changes require a governed amendment.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {availableInOrder.length === 0 && (
          <p style={S.muted}>No adjustments are available for the {spineName} economic model.</p>
        )}

        {availableInOrder.map(key => (
          <ModifierCard
            key={key}
            modKey={key}
            entry={form[key]}
            readOnly={readOnly}
            onToggle={handleToggle}
            onDraftChange={handleDraftChange}
            plan={plan}
            formErrors={{}}
          />
        ))}

        <UnavailableDisclosure unavailableKeys={unavailableInOrder} spineName={spineName} />
      </div>
    </section>
  );
});

// ---------------------------------------------------------------------------
// 11. NpsModifier — NPS Score Bands
// DDL: nps_tier (nps_tier_id int PK, plan_component_id int NN) [r64 confirmed]
//
// S929 G-17: authoring via D8 EP2b path. nps_router.py POST /nps/tiers is NOT
// called from this form. _materialize_modifier('nps') in plan_authoring_router.py
// handles INSERT into nps_tier using the caller's asyncpg conn.
//
// Score range: –100 to +100 (nps_score on nps_result — integer).
// payout_factor: 0.0–1.0. Bands should be non-overlapping (engine uses first match).
// role_id and plan_component_id resolved from router context — not in draftConfig.
// ---------------------------------------------------------------------------
function NpsModifier({ draft, onChange, errors = {} }) {
  const tiers = draft.tiers ?? [{ tierSequence: 1, tierLabel: '', fromScore: '', toScore: '', payoutFactor: '' }];

  const addTier = () => {
    onChange({ ...draft, tiers: [...tiers, { tierSequence: tiers.length + 1, tierLabel: '', fromScore: '', toScore: '', payoutFactor: '' }] });
  };

  const removeTier = (i) => {
    if (tiers.length <= 1) return;
    const next = tiers.filter((_, idx) => idx !== i).map((t, idx) => ({ ...t, tierSequence: idx + 1 }));
    onChange({ ...draft, tiers: next });
  };

  const updateTier = (i, field, value) => {
    onChange({ ...draft, tiers: tiers.map((t, idx) => idx === i ? { ...t, [field]: value } : t) });
  };

  // Overlap warning: check if any tier's fromScore < previous tier's toScore
  const hasOverlap = tiers.some((t, i) => {
    if (i === 0) return false;
    const prev = tiers[i - 1];
    return parseFloat(t.fromScore) < parseFloat(prev.toScore);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={S.infoBox}>
        Define NPS score bands. Each band maps a score range (–100 to +100) to a payout factor (0.0–1.0).
        Bands should be non-overlapping — the engine applies the first matching band for each rep's NPS score.
      </div>

      {hasOverlap && (
        <div style={{ ...S.warnBox, color: 'rgb(var(--color-warning))' }}>
          ⚠ Overlapping score bands detected. Ensure each band's From score is ≥ the previous band's To score.
        </div>
      )}

      {tiers.map((tier, i) => (
        <div key={i} style={S.card}>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ ...S.sectionH, margin: 0 }}>Band {tier.tierSequence}</p>
              {tiers.length > 1 && (
                <button type="button" onClick={() => removeTier(i)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-danger, #dc2626))', fontSize: 13 }}>
                  Remove
                </button>
              )}
            </div>
            <div style={S.grid2}>
              <Field label="From score *" id={`nps-from-${i}`} hint="–100 to +100, integer">
                <input id={`nps-from-${i}`} type="number" style={S.input}
                  value={tier.fromScore ?? ''} min={-100} max={100} step={1}
                  onChange={e => updateTier(i, 'fromScore', e.target.value)}
                  placeholder="-100" />
              </Field>
              <Field label="To score *" id={`nps-to-${i}`} hint="Must be > From score">
                <input id={`nps-to-${i}`} type="number" style={S.input}
                  value={tier.toScore ?? ''} min={-100} max={100} step={1}
                  onChange={e => updateTier(i, 'toScore', e.target.value)}
                  placeholder="0" />
              </Field>
            </div>
            <div style={S.grid2}>
              <Field label="Payout factor *" id={`nps-factor-${i}`} hint="0.0 = 0%, 1.0 = 100%">
                <input id={`nps-factor-${i}`} type="number" style={S.input}
                  value={tier.payoutFactor ?? ''} min={0} max={1} step={0.01}
                  onChange={e => updateTier(i, 'payoutFactor', e.target.value)}
                  placeholder="e.g. 0.80" />
              </Field>
              <Field label="Band label" id={`nps-label-${i}`} hint="Optional display label (max 30 chars)">
                <input id={`nps-label-${i}`} style={S.input} maxLength={30}
                  value={tier.tierLabel ?? ''}
                  onChange={e => updateTier(i, 'tierLabel', e.target.value)}
                  placeholder="e.g. Promoter" />
              </Field>
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="secondary-button" style={{ alignSelf: 'flex-start' }} onClick={addTier}>
        + Add band
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 12. AdoptionModifier — Adoption Milestones
// DDL: adoption_milestone (adoption_milestone_id int PK, plan_component_id int NN,
//      business_unit_id int NN, role_id int NN) [r64 confirmed]
//
// S929 G-18: authoring via D8 EP2b path. adoption_router.py POST /adoption/milestones
// is NOT called from this form. _materialize_modifier('adoption') in
// plan_authoring_router.py handles INSERT into adoption_milestone.
//
// business_unit_id [NN] — collected per milestone (Option A — owner decision S929).
// Fetches GET /api/v1/business-units on mount for dropdown population.
// milestone_unit DDL CHECK: count | percentage | score.
// role_id and plan_component_id resolved from router context — not in draftConfig.
// ---------------------------------------------------------------------------
function AdoptionModifier({ draft, onChange, errors = {} }) {
  const milestones = draft.milestones ?? [{ milestoneCode: '', milestoneName: '', milestoneDescription: '', milestoneTarget: '', milestoneUnit: 'count', payoutFactor: '', effectiveDate: '', endDate: '', fiscalPeriodId: '', businessUnitId: '' }];
  const [bus, setBus] = useState([]);
  const [busLoading, setBusLoading] = useState(true);

  useEffect(() => {
    fetch('/api/v1/business-units', { credentials: 'include' })
      .then(r => r.ok ? r.json() : { business_units: [] })
      .then(d => setBus(d.business_units ?? d.items ?? []))
      .catch(() => setBus([]))
      .finally(() => setBusLoading(false));
  }, []);

  const addMilestone = () => {
    onChange({ ...draft, milestones: [...milestones, { milestoneCode: '', milestoneName: '', milestoneDescription: '', milestoneTarget: '', milestoneUnit: 'count', payoutFactor: '', effectiveDate: '', endDate: '', fiscalPeriodId: '', businessUnitId: '' }] });
  };

  const removeMilestone = (i) => {
    if (milestones.length <= 1) return;
    onChange({ ...draft, milestones: milestones.filter((_, idx) => idx !== i) });
  };

  const updateMilestone = (i, field, value) => {
    onChange({ ...draft, milestones: milestones.map((m, idx) => idx === i ? { ...m, [field]: value } : m) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={S.infoBox}>
        Define one or more adoption milestones. Each milestone is scoped to a business unit and defines
        an achievement target. The payout factor is applied when the milestone is achieved (achieved_flag = true).
      </div>

      {milestones.map((m, i) => (
        <div key={i} style={S.card}>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ ...S.sectionH, margin: 0 }}>Milestone {i + 1}</p>
              {milestones.length > 1 && (
                <button type="button" onClick={() => removeMilestone(i)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-danger, #dc2626))', fontSize: 13 }}>
                  Remove
                </button>
              )}
            </div>

            <div style={S.grid2}>
              <Field label="Milestone code *" id={`adp-code-${i}`} hint="Max 20 chars — auto-uppercased">
                <input id={`adp-code-${i}`} style={S.input} maxLength={20}
                  value={m.milestoneCode ?? ''}
                  onChange={e => updateMilestone(i, 'milestoneCode', e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                  placeholder="e.g. FEATURE_ADOPTION" />
              </Field>
              <Field label="Milestone name *" id={`adp-name-${i}`} hint="Max 100 chars">
                <input id={`adp-name-${i}`} style={S.input} maxLength={100}
                  value={m.milestoneName ?? ''}
                  onChange={e => updateMilestone(i, 'milestoneName', e.target.value)}
                  placeholder="e.g. Feature Adoption Q1" />
              </Field>
            </div>

            <Field label="Description" id={`adp-desc-${i}`}>
              <input id={`adp-desc-${i}`} style={S.input}
                value={m.milestoneDescription ?? ''}
                onChange={e => updateMilestone(i, 'milestoneDescription', e.target.value)}
                placeholder="Optional description" />
            </Field>

            <div style={S.grid2}>
              <Field label="Target *" id={`adp-target-${i}`} hint="Achievement threshold value">
                <input id={`adp-target-${i}`} type="number" style={S.input} min={0} step={0.01}
                  value={m.milestoneTarget ?? ''}
                  onChange={e => updateMilestone(i, 'milestoneTarget', e.target.value)}
                  placeholder="e.g. 80" />
              </Field>
              <Field label="Unit *" id={`adp-unit-${i}`}>
                <select id={`adp-unit-${i}`} style={S.select}
                  value={m.milestoneUnit ?? 'count'}
                  onChange={e => updateMilestone(i, 'milestoneUnit', e.target.value)}>
                  <option value="count">Count</option>
                  <option value="percentage">Percentage</option>
                  <option value="score">Score</option>
                </select>
              </Field>
            </div>

            <div style={S.grid2}>
              <Field label="Payout factor *" id={`adp-factor-${i}`} hint="0.0 = 0%, 1.0 = 100%">
                <input id={`adp-factor-${i}`} type="number" style={S.input} min={0} max={1} step={0.01}
                  value={m.payoutFactor ?? ''}
                  onChange={e => updateMilestone(i, 'payoutFactor', e.target.value)}
                  placeholder="e.g. 1.0" />
              </Field>
              <Field label="Business unit *" id={`adp-bu-${i}`} hint="Required — milestone is scoped to a BU">
                {busLoading ? (
                  <input style={S.input} disabled value="Loading…" readOnly />
                ) : bus.length > 0 ? (
                  <select id={`adp-bu-${i}`} style={S.select}
                    value={m.businessUnitId ?? ''}
                    onChange={e => updateMilestone(i, 'businessUnitId', e.target.value)}>
                    <option value="">Select business unit</option>
                    {bus.map(b => (
                      <option key={b.business_unit_id ?? b.id} value={b.business_unit_id ?? b.id}>
                        {b.bu_name ?? b.name ?? String(b.business_unit_id ?? b.id)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input id={`adp-bu-${i}`} type="number" style={S.input}
                    value={m.businessUnitId ?? ''}
                    onChange={e => updateMilestone(i, 'businessUnitId', e.target.value)}
                    placeholder="Business unit ID" />
                )}
              </Field>
            </div>

            <div style={S.grid2}>
              <Field label="Effective date *" id={`adp-effdate-${i}`}>
                <input id={`adp-effdate-${i}`} type="date" style={S.input}
                  value={m.effectiveDate ?? ''}
                  onChange={e => updateMilestone(i, 'effectiveDate', e.target.value)} />
              </Field>
              <Field label="End date" id={`adp-enddate-${i}`} hint="Optional">
                <input id={`adp-enddate-${i}`} type="date" style={S.input}
                  value={m.endDate ?? ''}
                  onChange={e => updateMilestone(i, 'endDate', e.target.value)} />
              </Field>
            </div>

            <Field label="Fiscal period ID" id={`adp-period-${i}`} hint="Optional — leave blank to apply across periods">
              <input id={`adp-period-${i}`} type="number" style={S.input}
                value={m.fiscalPeriodId ?? ''}
                onChange={e => updateMilestone(i, 'fiscalPeriodId', e.target.value)}
                placeholder="Optional" />
            </Field>
          </div>
        </div>
      ))}

      <button type="button" className="secondary-button" style={{ alignSelf: 'flex-start' }} onClick={addMilestone}>
        + Add milestone
      </button>
    </div>
  );
}

// Default draft shapes per modifier — used when enabling for the first time
function getDefaultDraft(key) {
  switch (key) {
    case 'kicker':                 return { plans: [{ planName: '', appliesTo: 'total_payout', effectiveFrom: '', effectiveTo: '', isActive: true, rules: [] }] };
    case 'cap':                    return { maxPeriodPayout: null, maxPeriodPayoutCurrency: '' };
    case 'clawback':               return { policies: [{ policyName: '', protectionWindowDays: 90, clawbackPct: '', appliesToRevenueTypes: '', effectiveFrom: '', isActive: true }] };
    // BUG-1 FIX: bandDates added — one effectiveDate per revenue_type code.
    // Router reads bandDates[revTypeCode] for revenue_type_rate_table.effective_date.
    case 'rev_type_differentials': return { bands: {}, bandDates: {} };
    // S892 Wave 3: rules[] added to default draft shape. OD-13 / AD-56: Phase 1 = 1 rule.
    // tiers[] starts empty — CA must add at least one tier to pass modifierIsComplete.
    case 'spiff':                  return { plans: [{ planName: '', triggerModel: '', effectiveFrom: '', payoutCurrency: '', payoutCap: '', isActive: true, rules: [{ ruleName: '', tiers: [], productFamilyId: null, productLine: '', sellingMotionId: null, minDealAmount: '' }] }] };
    // S891 Wave 2: OI-S812-DRAW-RECOVERY-SCHEMA closed. recoverySchedule added to
    // default draft shape. drawType defaults to 'non_recoverable' — CA changes to
    // 'recoverable' to reveal RecoveryScheduleForm. OD-6 (S884): yearEndTreatment
    // defaults to 'carry_forward' — safest default for year-end balance treatment.
    case 'draw_guarantee':         return { plans: [{ planName: '', drawType: 'non_recoverable', advanceAmount: '', advanceCurrency: '', effectiveFrom: '', yearEndTreatment: 'carry_forward', recoverySchedule: { frequency: 'monthly', gracePeriods: 0, durationPeriods: '', recoveryCapPct: '', recoveryFloorAmount: null, recoveryFloorCurrency: null }, isActive: true }] };
    case 'contest':                return { contests: [{ contestName: '', contestCode: '', startDate: '', endDate: '', eligibilityType: 'role', rankMetric: '', tieBreakRule: '', prizes: [] }] };
    case 'non_cash':               return { awardTypeRefs: [] };
    case 'multi_year_deal':        return { policies: [{ policyName: '', payoutBasis: '', amortizationPeriods: '', maxContractYears: 5, capFutureCreditToCurrentQuota: false, effectiveFrom: '', commissionRate: '', isActive: true }] };
    // S913 G-01: MBO objectives default shape. scoringMethod defaults to 'binary' — most common case.
    // tiers[] starts empty — only needed when scoringMethod === 'tiered'.
    case 'mbo':                    return { objectives: [{ mboCode: '', mboName: '', mboWeight: '', scoringMethod: 'binary', effectiveDate: '', tiers: [] }] };
    // S929 G-17: NPS tier bands default shape. tiers[] starts with one empty band.
    // fromScore/toScore: integer –100 to 100. payoutFactor: 0.0–1.0.
    // role_id and plan_component_id resolved from router context — not stored in draftConfig.
    case 'nps':                    return { tiers: [{ tierSequence: 1, tierLabel: '', fromScore: '', toScore: '', payoutFactor: '' }] };
    // S929 G-18: Adoption milestone default shape. milestones[] starts with one empty milestone.
    // businessUnitId: required per milestone (Option A — owner decision S929).
    // milestone_unit DDL CHECK: count | percentage | score.
    case 'adoption':               return { milestones: [{ milestoneCode: '', milestoneName: '', milestoneDescription: '', milestoneTarget: '', milestoneUnit: 'count', payoutFactor: '', effectiveDate: '', endDate: '', fiscalPeriodId: '', businessUnitId: '' }] };
    default:                       return {};
  }
}

PayoutAdjustmentsSection.displayName = 'PayoutAdjustmentsSection';

PayoutAdjustmentsSection.propTypes = {
  plan: PropTypes.shape({
    planId:       PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    primarySpine: PropTypes.string,
    // OI-S814-CAP-DISPLAY-AFTER-MATERIALIZE: plan_component data from EP2 for cap re-seeding
    planComponent: PropTypes.shape({
      max_period_payout:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      max_period_payout_currency: PropTypes.string,
      maxPeriodPayout:            PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      maxPeriodPayoutCurrency:    PropTypes.string,
    }),
    // S895: draw ledger participant initialization context (T3-5)
    planStatus:     PropTypes.string,
    userRole:       PropTypes.oneOf(['comp_admin', 'manager']),
    managerId:      PropTypes.string,
    fiscalPeriodId: PropTypes.number,
  }).isRequired,
  layer2Config: PropTypes.shape({
    modifiers: PropTypes.arrayOf(PropTypes.shape({
      modifierType: PropTypes.string.isRequired,
      enabled:      PropTypes.bool,
      entityRefs:   PropTypes.array,
      draftConfig:  PropTypes.object,
    })),
  }).isRequired,
  pcmaMatrix: PropTypes.shape({
    availableModifiers: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  readOnly: PropTypes.bool.isRequired,
};

export default PayoutAdjustmentsSection;
